import { updateRoomEnvironment, checkStageAdvance, calculateHarvestQuality, calculateFinalYield } from './systems/environmental';
import { getLaborReport, applyUnderstaffingPenalty, processWages, refreshJobMarket } from './systems/labor';
import { processWeeklyFinances, updateMarketPrices, checkContractDeadlines, calculateHarvestValue, calculateRepGain, checkStageProgression, LICENSE_COSTS, VIOLATION_FINES, calculateReviewScore } from './systems/economics';
import { rollRandomEvents, applyEventDamage, resolveRoomInspection } from './systems/events';
import { generateContract, getAvailableTemplates } from './data/contracts';
import { getStrain } from './data/strains';
import { getRecipe } from './data/strainRecipes';
import { getAvailableReviewers, getReviewQuote } from './data/reviewers';
import { checkTutorialCompletion } from './systems/tutorial';
import { generateDirective, checkDirectiveProgress, evaluateDirectiveCompletion, getSatisfactionLevel, calculateBuybackPrice } from './systems/corporate';
import { rollPhenoCandidates } from './systems/genetics';
import { processCuringDay } from './systems/curing';
import { processProcessingDay } from './systems/processing';
import { advanceCampaignDays, resolveCompletedCampaign, weeklyMarketingTick, evaluateTourSnapshot, getTourMessage, WEEKLY_BRAND_CAP, MAX_TOURS_PER_MONTH, TOUR_FEE, TOUR_CANCEL_REFUND } from './systems/marketing';
import { CAMPAIGN_TEMPLATES } from './data/marketingCampaigns';
import { hasMarketingDept } from './systems/marketingHelpers';

let batchIdCounter = 10;
let inventoryIdCounter = 1;

function gameReducerCore(state, action) {
  switch (action.type) {

    // ── TICK ────────────────────────────────────────────────────────────────
    case 'TICK': {
      if (state.paused || state.isShutDown) return state;

      const newTick = state.tick + 1;
      // 1 tick = 1 game hour; 24 ticks = 1 day; 168 ticks = 1 week
      const newGameDay  = Math.floor(newTick / 24) + 1;
      const newGameWeek = Math.floor(newTick / 168) + 1;
      const weekAdvanced = newGameWeek > state.gameWeek;

      // Environmental updates per room (skip if temporarily closed by inspection)
      let updatedRooms = state.rooms.map(room => {
        if (!room.unlocked) return room;
        if (room.closedUntilDay && newGameDay <= room.closedUntilDay) return room;
        return updateRoomEnvironment(room, state.unlockedTech);
      });

      // Stage advance checks for batches
      updatedRooms = updatedRooms.map(room => {
        if (!room.batch) return room;
        const strain = getStrain(room.batch.strainId);
        if (!strain) return room;
        const nextStage = checkStageAdvance(room.batch, strain);
        if (nextStage) {
          return {
            ...room,
            batch: { ...room.batch, stage: nextStage, dayInStage: 0 },
            alerts: [...room.alerts, {
              type: 'info',
              message: `Batch advanced to ${nextStage}`,
              severity: 'info',
            }],
          };
        }
        return room;
      });

      // Labor coverage and understaffing penalties (skip closed rooms)
      const laborReports = getLaborReport(updatedRooms, state.workers, state.unlockedTech);
      const reportByRoom = new Map(laborReports.map(r => [r.roomId, r]));
      updatedRooms = updatedRooms.map(room => {
        if (room.closedUntilDay && newGameDay <= room.closedUntilDay) return room;
        const report = reportByRoom.get(room.id);
        if (!report || report.deficit <= 0 || !room.batch) return room;
        return { ...room, batch: applyUnderstaffingPenalty(room.batch, report.deficit) };
      });

      // Random event rolls (only check every ~6 ticks to reduce overhead)
      let newActiveEvents = state.activeEvents;
      let newNotifications = state.notifications;
      if (newTick % 6 === 0) {
        const { newEvents, newNotifications: notifs } = rollRandomEvents({
          ...state, rooms: updatedRooms, tick: newTick, gameDay: newGameDay
        });
        newActiveEvents = [...state.activeEvents, ...newEvents];
        newNotifications = [...state.notifications, ...notifs].slice(-50); // cap at 50
      }

      // Apply event damage to rooms
      updatedRooms = applyEventDamage(updatedRooms, newActiveEvents);

      // Sync poweredDown flag — rooms with an active unresolved power_outage event lose power
      const poweredDownRoomIds = new Set(
        newActiveEvents.filter(e => e.type === 'power_outage' && !e.resolved).map(e => e.roomId)
      );
      updatedRooms = updatedRooms.map(room => {
        const shouldBePoweredDown = poweredDownRoomIds.has(room.id);
        if (room.poweredDown === shouldBePoweredDown) return room;
        return { ...room, poweredDown: shouldBePoweredDown };
      });

      // Apply morale penalty to workers in rooms with active equipment failures
      const equipFailRoomIds = new Set(
        newActiveEvents.filter(e => e.type === 'equipment_failure' && !e.resolved).map(e => e.roomId)
      );
      let updatedWorkers = state.workers.map(w =>
        equipFailRoomIds.has(w.assignedRoomId)
          ? { ...w, morale: Math.max(0, w.morale - 0.5) }
          : w
      );

      // Active research progress
      let newResearch = state.activeResearch;
      if (newResearch && newTick % 168 === 0) {
        // RP accumulates weekly — handled in PROCESS_WEEK
      }

      // ── Publish due reviews ──────────────────────────────────────────────
      let newPendingReviews = state.pendingReviews || [];
      let newPublishedReviews = state.publishedReviews || [];
      let repFromReviews = 0;
      const dueReviews = newPendingReviews.filter(r => r.publishDay <= newGameDay);
      if (dueReviews.length > 0) {
        newPendingReviews = newPendingReviews.filter(r => r.publishDay > newGameDay);
        for (const pending of dueReviews) {
          const { getReviewer } = require('./data/reviewers');
          const { getStrain: getStrainForReview } = require('./data/strains');
          const reviewer = getReviewer(pending.reviewerId);
          if (!reviewer) continue;
          const rawScore = calculateReviewScore(pending.inventorySnapshot, reviewer, state.illegalSalesCount);
          // Cap review scores by current company stage to represent early-stage credibility limits
          const REVIEW_CAPS = { 1: 75, 2: 85, 3: 92, 4: 98 };
          const cap = REVIEW_CAPS[state.stage] || 100;
          const score = Math.min(rawScore, cap);
          const quote = getReviewQuote(reviewer, score);
          const repImpact = score >= 80 ? Math.round(reviewer.reputationWeight * 4)
            : score >= 60 ? Math.round(reviewer.reputationWeight * 1)
            : score >= 40 ? Math.round(reviewer.reputationWeight * -1)
            : Math.round(reviewer.reputationWeight * -4);
          repFromReviews += repImpact;
          const reviewStrain = getStrainForReview(pending.inventorySnapshot?.strainId);
          newPublishedReviews = [
            { id: pending.id, reviewerId: reviewer.id, reviewerName: reviewer.name,
              reviewerHandle: reviewer.handle, reviewerEmoji: reviewer.emoji,
              score, quote, channel: pending.channel, day: newGameDay, repImpact,
              strainId: pending.inventorySnapshot?.strainId ?? null,
              strainName: reviewStrain?.name ?? null },
            ...newPublishedReviews,
          ].slice(0, 20); // keep last 20
          newNotifications = [
            ...newNotifications,
            { id: `notif-review-${Date.now()}-${pending.id}`, type: repImpact >= 0 ? 'success' : 'warning', day: newGameDay, read: false,
              message: `${reviewer.emoji} ${reviewer.name} reviewed your drop — ${score}/100: "${quote}"` },
          ].slice(-50);
        }
      }

      // Weekly processing
      if (weekAdvanced) {
        return processWeek({
          ...state,
          tick: newTick,
          gameDay: newGameDay,
          gameWeek: newGameWeek,
          rooms: updatedRooms,
          activeEvents: newActiveEvents,
          notifications: newNotifications,
          pendingReviews: newPendingReviews,
          publishedReviews: newPublishedReviews,
          reputation: Math.max(0, Math.min(100, state.reputation + repFromReviews)),
        });
      }

      // ── Buyout offer at Year 1 (day 365) ────────────────────────────────
      let buyoutOfferShown = state.buyoutOfferShown;
      if (!buyoutOfferShown && newGameDay >= 365 && state.gameDay < 365) {
        buyoutOfferShown = true;
        newActiveEvents = [...newActiveEvents, {
          id: 'event-buyout-365',
          type: 'buyout_offer',
          label: '$1,000,000 Acquisition Offer',
          icon: '💎',
          description: 'CannaVentures Capital wants to acquire your operation for $1,000,000.',
          severity: 'milestone',
          startDay: newGameDay,
          resolved: false,
          resolvedDay: null,
        }];
        newNotifications = [...newNotifications, {
          id: `notif-buyout-${Date.now()}`,
          type: 'milestone',
          day: newGameDay,
          read: false,
          message: '💎 Year 1 complete! CannaVentures Capital has made a $1,000,000 offer to acquire your operation.',
        }].slice(-50);
      }

      // ── Expire room closures ─────────────────────────────────────────────
      updatedRooms = updatedRooms.map(room => {
        if (room.closedUntilDay && newGameDay > room.closedUntilDay) {
          return { ...room, closedUntilDay: null };
        }
        return room;
      });

      // ── Manager automation: auto-plant and auto-harvest ─────────────────────
      let autoRooms = updatedRooms;
      let autoInventory = [...state.inventory];
      let autoSeeds = { ...state.seedInventory };
      let autoNotifs = [...newNotifications];
      let autoWorkers = updatedWorkers;

      autoRooms = autoRooms.map(room => {
        const auto = room.automation;
        if (!auto?.enabled || !auto.managerId) return room;

        const manager = state.workers.find(w => w.id === auto.managerId);
        if (!manager) return room;

        // Resolve active strain recipe (if purchased and assigned)
        const activeRecipe = (auto.recipeId && (state.purchasedRecipes ?? []).includes(auto.recipeId))
          ? getRecipe(auto.recipeId)
          : null;

        // ── Recipe: scheduled actions (defoliate / flush / treat_pests) ───────
        // Execute each matching action exactly once per game-day (fires on the tick
        // where dayInStage crosses the integer boundary: fractional part < 1/24).
        let batchWithActions = room.batch ? { ...room.batch } : null;

        // ── Recipe: apply stage environment targets each tick ─────────────────────
        // This is what makes recipes actually control conditions to hit yield targets.
        let roomRecipeOverride = null;
        if (activeRecipe && batchWithActions && batchWithActions.stage !== 'harvest') {
          const stageTargets = activeRecipe.stages[batchWithActions.stage];
          if (stageTargets) {
            roomRecipeOverride = {
              ...room.recipe,
              tempTarget:      stageTargets.temp,
              humidityTarget:  stageTargets.humidity,
              co2Target:       stageTargets.co2,
              lightHours:      stageTargets.lightHours,
              ec:              stageTargets.ec,
              ph:              stageTargets.ph,
            };
          }
        }

        if (activeRecipe && batchWithActions && batchWithActions.stage !== 'harvest') {
          const stageActions = activeRecipe.actions.filter(a => a.stage === batchWithActions.stage);
          const currentDayInStage = batchWithActions.dayInStage;
          // Fire once per day: only on the tick where we first cross the integer boundary
          const isNewDay = currentDayInStage % 1 < (1 / 24);
          if (isNewDay) {
            const dayInt = Math.floor(currentDayInStage);
            stageActions
              .filter(a => a.dayInStage === dayInt)
              .forEach(a => {
                // Skill gating: low-skill managers (<40) only handle flip_lights
                if (manager.skill < 40 && a.type !== 'flip_lights') return;
                if (a.type === 'defoliate') {
                  batchWithActions.yieldPotential = Math.min(100, (batchWithActions.yieldPotential ?? 100) + 3);
                  batchWithActions.moldRisk        = Math.max(0, (batchWithActions.moldRisk ?? 0) - 5);
                  batchWithActions.pestRisk        = Math.max(0, (batchWithActions.pestRisk ?? 0) - 3);
                  autoNotifs = [...autoNotifs, {
                    id: `notif-recipe-defoliate-${Date.now()}-${room.id}`,
                    message: `✂️ ${room.name}: ${manager.name} defoliated per recipe (day ${dayInt})`,
                    type: 'info', day: newGameDay, read: false,
                  }].slice(-50);
                } else if (a.type === 'flush') {
                  batchWithActions.ec = 0;
                  autoNotifs = [...autoNotifs, {
                    id: `notif-recipe-flush-${Date.now()}-${room.id}`,
                    message: `💧 ${room.name}: ${manager.name} flushed nutrients per recipe`,
                    type: 'info', day: newGameDay, read: false,
                  }].slice(-50);
                } else if (a.type === 'treat_pests') {
                  batchWithActions.pestRisk = Math.max(0, (batchWithActions.pestRisk ?? 0) - 30);
                  batchWithActions.moldRisk = Math.max(0, (batchWithActions.moldRisk ?? 0) - 20);
                  autoNotifs = [...autoNotifs, {
                    id: `notif-recipe-treat-${Date.now()}-${room.id}`,
                    message: `🌿 ${room.name}: ${manager.name} applied prophylactic IPM per recipe`,
                    type: 'info', day: newGameDay, read: false,
                  }].slice(-50);
                } else if (a.type === 'flip_lights') {
                  if (!roomRecipeOverride) roomRecipeOverride = { ...room.recipe };
                  roomRecipeOverride = { ...roomRecipeOverride, lightHours: 12 };
                  autoNotifs = [...autoNotifs, {
                    id: `notif-recipe-flip-${Date.now()}-${room.id}`,
                    message: `🌙 ${room.name}: ${manager.name} flipped to 12/12 per recipe`,
                    type: 'info', day: newGameDay, read: false,
                  }].slice(-50);
                }
              });
          }
        }

        // ── All managers flip lights when veg is complete ─────────────────────
        if (batchWithActions?.stage === 'vegetative') {
          const flipAtDay = activeRecipe?.transferAtVegDay
            ?? getStrain(batchWithActions.strainId)?.veg?.days
            ?? 21;
          const currentDayInt = Math.floor(batchWithActions.dayInStage);
          const isNewDay2 = batchWithActions.dayInStage % 1 < (1 / 24);
          const lightAlreadyFlipped = (roomRecipeOverride ?? room.recipe)?.lightHours <= 13;
          if (isNewDay2 && currentDayInt >= flipAtDay && !lightAlreadyFlipped) {
            if (!roomRecipeOverride) roomRecipeOverride = { ...room.recipe };
            roomRecipeOverride = { ...roomRecipeOverride, lightHours: 12 };
            const notifId = `notif-lights-flip-${room.id}-day${currentDayInt}`;
            if (!autoNotifs.some(n => n.id === notifId)) {
              autoNotifs = [...autoNotifs, {
                id: notifId,
                message: `🌙 ${room.name}: ${manager.name} flipped lights to 12/12 — flowering begins`,
                type: 'info', day: newGameDay, read: false,
              }].slice(-50);
            }
          }
        }

        // ── Recipe: early harvest trigger ─────────────────────────────────────
        if (activeRecipe && batchWithActions?.stage === 'flowering') {
          if (Math.floor(batchWithActions.dayInStage) >= activeRecipe.harvestAtFlowerDay) {
            batchWithActions = { ...batchWithActions, stage: 'harvest' };
          }
        }

        // Propagate batch mutations back to room before harvest check
        const roomWithBatch = batchWithActions && batchWithActions !== room.batch
          ? { ...room, batch: batchWithActions }
          : room;

        // Auto-harvest: when batch reaches harvest stage
        if (roomWithBatch.batch?.stage === 'harvest') {
          const skillMod = manager.skill >= 75 ? 1.08 : manager.skill >= 50 ? 1.0 : manager.skill >= 30 ? 0.88 : 0.75;
          const strainAuto = getStrain(roomWithBatch.batch.strainId);
          const harvestedGrams = calculateFinalYield(roomWithBatch.batch, strainAuto, manager.skill);
          const baseQuality = Math.round(calculateHarvestQuality(roomWithBatch.batch, strainAuto) * skillMod);
          // Apply recipe quality bonus if active
          const recipeBonus = activeRecipe ? activeRecipe.qualityBonus : 0;
          const qualityScore = Math.min(100, baseQuality + recipeBonus);
          const invId = `inv-${inventoryIdCounter++}`;
          autoInventory = [...autoInventory, {
            id: invId,
            batchId: roomWithBatch.batch.id,
            strainId: roomWithBatch.batch.strainId,
            grams: harvestedGrams,
            qualityScore,
            thcAccumulation: roomWithBatch.batch.thcAccumulation,
            terpeneProfile: roomWithBatch.batch.terpeneProfile,
            visualAppeal: roomWithBatch.batch.visualAppeal,
            harvestDay: newGameDay,
            cureDay: null,
            status: 'fresh',
            corporateBacked: state.corporateInvestmentAccepted && !state.corporateBoughtOut,
          }];
          autoNotifs = [...autoNotifs, {
            id: `notif-auto-harvest-${Date.now()}-${room.id}`,
            message: `📦 ${room.name}: auto-harvested ${harvestedGrams}g (quality ${qualityScore}${recipeBonus > 0 ? ` +${recipeBonus} recipe bonus` : ''}) — ready to sell`,
            type: 'success',
            day: newGameDay,
            read: false,
          }].slice(-50);
          return { ...room, batch: null, needsCleaning: true, alerts: [] };
        }

        // If batch was mutated by recipe actions or room recipe was updated, return updated room
        const baseRoom = (batchWithActions && batchWithActions !== room.batch)
          ? { ...room, batch: batchWithActions }
          : room;
        if (roomRecipeOverride) {
          return { ...baseRoom, recipe: roomRecipeOverride };
        }
        if (baseRoom !== room) return baseRoom;

        // Auto-plant: when room is empty and clean
        if (!room.batch && !room.needsCleaning && (autoSeeds[auto.defaultStrainId] ?? 0) > 0) {
          const count = Math.min(auto.defaultPlantCount, autoSeeds[auto.defaultStrainId], room.capacity || 50);
          autoSeeds = { ...autoSeeds, [auto.defaultStrainId]: autoSeeds[auto.defaultStrainId] - count };
          const newBatch = {
            id: `batch-auto-${++batchIdCounter}`,
            strainId: auto.defaultStrainId,
            plantCount: count,
            stage: 'seedling',
            dayInStage: 0,
            totalAge: 0,
            health: 95,
            stress: 5,
            stressHistory: [],
            yieldPotential: 100,
            thcAccumulation: 0,
            terpeneProfile: 50,
            visualAppeal: 80,
            hermaphroditeRisk: 0,
            moldRisk: 0,
            pestRisk: 0,
            projectedGrams: count * 8,
            harvestedGrams: null,
            qualityScore: null,
            selectedPhenoId: null,
            phenoBonuses: null,
          };
          autoNotifs = [...autoNotifs, {
            id: `notif-auto-plant-${Date.now()}-${room.id}`,
            message: `🤖 ${room.name}: manager auto-planted ${count} ${auto.defaultStrainId.replace(/-/g, ' ')} plants`,
            type: 'info',
            day: newGameDay,
            read: false,
          }].slice(-50);
          const plantedRoom = { ...room, batch: newBatch };
          return roomRecipeOverride ? { ...plantedRoom, recipe: roomRecipeOverride } : plantedRoom;
        }

        // Seeds depleted — notify once
        if (!room.batch && !room.needsCleaning && (autoSeeds[auto.defaultStrainId] ?? 0) <= 0) {
          const alreadyNotified = autoNotifs.some(n => n.id?.includes(`seeds-needed-${room.id}`));
          if (!alreadyNotified) {
            autoNotifs = [...autoNotifs, {
              id: `seeds-needed-${room.id}`,
              message: `🌱 ${room.name}: needs seeds — buy more ${auto.defaultStrainId.replace(/-/g, ' ')} to resume automation`,
              type: 'warning',
              day: newGameDay,
              read: false,
            }].slice(-50);
          }
        }

        return roomRecipeOverride ? { ...room, recipe: roomRecipeOverride } : room;
      });

      // ── High-skill manager batch transfers (skill ≥ 70) ───────────────────
      // Best managers transfer veg batches to flower rooms and follow the batch.
      for (let ti = 0; ti < autoRooms.length; ti++) {
        const tRoom = autoRooms[ti];
        const tAuto = tRoom.automation;
        if (!tAuto?.enabled || !tAuto.managerId) continue;

        const tManager = autoWorkers.find(w => w.id === tAuto.managerId);
        if (!tManager || tManager.skill < 70) continue;

        const tBatch = tRoom.batch;
        if (!tBatch || tBatch.stage !== 'vegetative') continue;

        const tRecipe = (tAuto.recipeId && (state.purchasedRecipes ?? []).includes(tAuto.recipeId))
          ? getRecipe(tAuto.recipeId) : null;
        const tFlipAtDay = tRecipe?.transferAtVegDay ?? getStrain(tBatch.strainId)?.veg?.days ?? 21;
        const tIsTransferDay = tBatch.dayInStage % 1 < (1 / 24) && Math.floor(tBatch.dayInStage) >= tFlipAtDay;
        if (!tIsTransferDay) continue;

        // Find an empty, clean, unlocked, non-lab room to transfer into
        const destIdx = autoRooms.findIndex(r =>
          r.id !== tRoom.id &&
          r.unlocked &&
          !r.batch &&
          !r.needsCleaning &&
          r.id !== 'room-lab' &&
          !r.closedUntilDay
        );
        if (destIdx === -1) continue; // No room available; light flip already handled above

        const destRoom = autoRooms[destIdx];
        const transferredBatch = { ...tBatch, stage: 'flowering', dayInStage: 0 };

        // Destination room: receives batch, manager takes over, lights set to 12/12
        autoRooms[destIdx] = {
          ...destRoom,
          batch: transferredBatch,
          recipe: { ...(destRoom.recipe || {}), lightHours: 12 },
          automation: {
            enabled: true,
            managerId: tAuto.managerId,
            defaultStrainId: tAuto.defaultStrainId,
            defaultPlantCount: tAuto.defaultPlantCount,
            recipeId: tAuto.recipeId,
          },
        };

        // Source room: batch cleared (auto-plant handles re-seeding), manager leaves
        autoRooms[ti] = {
          ...tRoom,
          batch: null,
          automation: { ...tAuto, managerId: null, enabled: false },
        };

        // Manager follows the batch to the new room
        autoWorkers = autoWorkers.map(w =>
          w.id === tAuto.managerId ? { ...w, assignedRoomId: destRoom.id } : w
        );

        autoNotifs = [...autoNotifs, {
          id: `notif-transfer-${Date.now()}-${tRoom.id}`,
          message: `🔄 ${tManager.name} transferred batch: ${tRoom.name} → ${destRoom.name} (12/12 flowering)`,
          type: 'success', day: newGameDay, read: false,
        }].slice(-50);
      }

      // Tutorial auto-advance: DAY_7 and STAGE_FLOWERING checks
      let newTutorial = state.tutorial;
      if (newTutorial?.enabled) {
        const dayCheck = checkTutorialCompletion(newTutorial, '_TIME_CHECK', null, newGameDay);
        if (dayCheck !== null) newTutorial = { ...newTutorial, currentStep: dayCheck };
        const floweringRoom = autoRooms.find(r => r.batch?.stage === 'flowering' && state.rooms.find(old => old.id === r.id)?.batch?.stage !== 'flowering');
        if (floweringRoom) {
          const flowerCheck = checkTutorialCompletion(newTutorial, '_BATCH_FLOWERING', null, newGameDay);
          if (flowerCheck !== null) newTutorial = { ...newTutorial, currentStep: flowerCheck };
        }
      }

      // ── Daily simulation: curing + processing advance once per game-day ──────────
      const dayAdvanced = newGameDay > state.gameDay;
      let dailyRooms = autoRooms;
      let dailyInventory = autoInventory;
      let dailyNotifs = autoNotifs;

      if (dayAdvanced) {
        const preCuring = {
          ...state, rooms: dailyRooms, inventory: dailyInventory,
          notifications: dailyNotifs, gameDay: newGameDay,
        };
        const afterCuring = processCuringDay(preCuring);
        dailyRooms    = afterCuring.rooms;
        dailyInventory = afterCuring.inventory;
        dailyNotifs   = afterCuring.notifications;

        const preProcessing = {
          ...afterCuring, rooms: dailyRooms, inventory: dailyInventory,
          notifications: dailyNotifs, gameDay: newGameDay,
        };
        const afterProcessing = processProcessingDay(preProcessing);
        dailyRooms    = afterProcessing.rooms;
        dailyInventory = afterProcessing.inventory;
        dailyNotifs   = afterProcessing.notifications;

        // ── Marketing: advance campaign days ──────────────────────────────
        const mktState = state.marketing || {};
        if ((mktState.activeCampaigns || []).length > 0) {
          const { marketing: updatedMkt, completedCampaigns } = advanceCampaignDays(mktState);
          let mktBrandDelta = 0;
          let mktDemandDelta = 0;

          for (const completed of completedCampaigns) {
            // Average quality of recent sales for quality check
            const recentSales = (state.salesHistory || []).slice(-10);
            const avgQuality = recentSales.length > 0
              ? recentSales.reduce((s, sale) => s + (sale.qualityScore || 50), 0) / recentSales.length
              : 50;
            const result = resolveCompletedCampaign(
              completed, avgQuality, updatedMkt.socialSaturation || 0, Math.random
            );
            mktBrandDelta += result.brandDelta;
            mktDemandDelta += result.demandDelta;
            updatedMkt.socialSaturation = (updatedMkt.socialSaturation || 0) + result.saturationIncrease;
            updatedMkt.completedCampaignHistory = [
              ...(updatedMkt.completedCampaignHistory || []),
              { ...completed, result: result.backlash ? 'backlash' : 'success', resolvedDay: newGameDay },
            ].slice(-20);
            dailyNotifs = [...dailyNotifs, {
              id: `notif-campaign-done-${Date.now()}-${completed.id}`,
              message: result.message, type: result.backlash ? 'warning' : 'success',
              day: newGameDay, read: false,
            }];
          }

          state = {
            ...state,
            marketing: updatedMkt,
            brandValue: Math.max(0, Math.min(100, state.brandValue + Math.min(WEEKLY_BRAND_CAP, mktBrandDelta))),
            demandModifier: Math.max(0.85, Math.min(1.30, (state.demandModifier ?? 1.0) + mktDemandDelta)),
          };
        }

        // ── Marketing: resolve scheduled tours ────────────────────────────
        const tours = (state.marketing?.scheduledTours || []);
        const dueTours = tours.filter(t => t.scheduledDay <= newGameDay);
        if (dueTours.length > 0) {
          let tourBrandDelta = 0;
          let tourDemandDelta = 0;
          let tourRepDelta = 0;
          for (const tour of dueTours) {
            const tourResult = evaluateTourSnapshot({ ...state, rooms: dailyRooms });
            tourBrandDelta += tourResult.brandDelta;
            tourDemandDelta += tourResult.demandDelta;
            tourRepDelta += tourResult.repDelta;
            dailyNotifs = [...dailyNotifs, {
              id: `notif-tour-done-${Date.now()}-${tour.id}`,
              message: getTourMessage(tourResult.narrative),
              type: tourResult.narrative === 'tour_bad' ? 'warning' : 'success',
              day: newGameDay, read: false,
            }];
          }
          state = {
            ...state,
            marketing: {
              ...state.marketing,
              scheduledTours: tours.filter(t => t.scheduledDay > newGameDay),
            },
            brandValue: Math.max(0, Math.min(100, state.brandValue + tourBrandDelta)),
            demandModifier: Math.max(0.85, Math.min(1.30, (state.demandModifier ?? 1.0) + tourDemandDelta)),
            reputation: Math.max(0, Math.min(100, state.reputation + tourRepDelta)),
          };
        }
      }

      return {
        ...state,
        tick: newTick,
        gameDay: newGameDay,
        gameWeek: newGameWeek,
        rooms: dailyRooms,
        workers: autoWorkers,
        inventory: dailyInventory,
        seedInventory: autoSeeds,
        activeEvents: newActiveEvents,
        notifications: dailyNotifs.slice(-50),
        pendingReviews: newPendingReviews,
        publishedReviews: newPublishedReviews,
        reputation: Math.max(0, Math.min(100, state.reputation + repFromReviews)),
        tutorial: newTutorial,
        buyoutOfferShown,
      };
    }

    // ── SET_RECIPE_TARGET ───────────────────────────────────────────────────
    case 'SET_RECIPE_TARGET': {
      const { roomId, parameter, value } = action.payload;
      return {
        ...state,
        rooms: state.rooms.map(room =>
          room.id === roomId
            ? { ...room, recipe: { ...room.recipe, [parameter]: value } }
            : room
        ),
      };
    }

    // ── TOGGLE_LIGHT ────────────────────────────────────────────────────────
    case 'TOGGLE_LIGHT': {
      const { roomId } = action.payload;
      const roomForLight = state.rooms.find(r => r.id === roomId);
      if (!roomForLight) return state;
      // Artificial lights can only be toggled if they're installed
      if (!roomForLight.equipment?.lights?.installed) {
        return {
          ...state,
          notifications: [
            ...state.notifications,
            { id: `notif-nolight-${Date.now()}`, message: `⚠️ No lights installed in ${roomForLight.name}. Buy an LED Array from the Equipment Shop first.`, type: 'warning', day: state.gameDay, read: false },
          ],
        };
      }
      return {
        ...state,
        rooms: state.rooms.map(r =>
          r.id === roomId
            ? { ...r, environment: { ...r.environment, lightOn: !r.environment.lightOn } }
            : r
        ),
      };
    }

    // ── BUY_EQUIPMENT ───────────────────────────────────────────────────────
    case 'BUY_EQUIPMENT': {
      const { roomId, itemId } = action.payload;
      const roomForEquip = state.rooms.find(r => r.id === roomId);
      if (!roomForEquip || !roomForEquip.unlocked) return state;
      const { getEquipmentItem } = require('./data/recipes');
      const item = getEquipmentItem(itemId);
      if (!item) return state;
      if (state.cash < item.cost) return state;
      if (itemId === 'co2-system' && !state.unlockedTech.includes('A2')) {
        return {
          ...state,
          notifications: [
            ...state.notifications,
            { id: `notif-co2-blocked-${Date.now()}`, message: '⚠ CO₂ Injection requires CO₂ Control tech (A2). Research it in the lab first.', type: 'warning', day: state.gameDay, read: false },
          ],
        };
      }
      const isLightInstall = item.targetEquipment === 'lights' && item.effect.installed;
      return {
        ...state,
        cash: state.cash - item.cost,
        totalExpenses: state.totalExpenses + item.cost,
        rooms: state.rooms.map(r => {
          if (r.id !== roomId) return r;
          return {
            ...r,
            equipment: {
              ...r.equipment,
              [item.targetEquipment]: {
                ...(r.equipment[item.targetEquipment] || {}),
                ...item.effect,
                health: 100,
              },
            },
            // Auto-turn lights on when installed so plants don't die in the dark
            environment: isLightInstall
              ? { ...r.environment, lightOn: true }
              : r.environment,
          };
        }),
        notifications: [
          ...state.notifications,
          { id: `notif-equip-${Date.now()}`, message: `${item.icon} ${item.name} installed in ${roomForEquip.name}${isLightInstall ? ' — lights turned on' : ''}!`, type: 'success', day: state.gameDay, read: false },
        ],
      };
    }

    // ── APPLY_RECIPE ─────────────────────────────────────────────────────────
    case 'APPLY_RECIPE': {
      const { roomId, packageId } = action.payload;
      const roomForRecipe = state.rooms.find(r => r.id === roomId);
      if (!roomForRecipe || !roomForRecipe.unlocked) return state;
      const { getRecipePackage } = require('./data/recipes');
      const pkg = getRecipePackage(packageId);
      if (!pkg) return state;
      if (state.cash < pkg.cost) return state;
      return {
        ...state,
        cash: state.cash - pkg.cost,
        totalExpenses: state.totalExpenses + pkg.cost,
        rooms: state.rooms.map(r => {
          if (r.id !== roomId) return r;
          return {
            ...r,
            recipe: {
              ...r.recipe,
              ...pkg.recipeChanges,
            },
          };
        }),
        notifications: [
          ...state.notifications,
          { id: `notif-recipe-${Date.now()}`, message: `${pkg.icon} ${pkg.name} applied to ${roomForRecipe.name}!`, type: 'success', day: state.gameDay, read: false },
        ],
      };
    }

    // ── PLANT_BATCH ─────────────────────────────────────────────────────────
    case 'PLANT_BATCH': {
      const { roomId, strainId, plantCount, phenoId } = action.payload;
      const seedCount = plantCount || 4;
      const seedInv = { ...state.seedInventory };
      if (!seedInv[strainId] || seedInv[strainId] < seedCount) return state; // not enough seeds
      seedInv[strainId] = seedInv[strainId] - seedCount;

      const plantRoom = state.rooms.find(r => r.id === roomId);
      const isDirty = plantRoom?.needsCleaning ?? false;

      // Snapshot pheno bonuses at plant time. Both selectedPhenoId and phenoBonuses are
      // stored on the batch: selectedPhenoId for record-keeping, phenoBonuses for direct
      // use in environmental.js (no state.phenotypes lookup needed per tick).
      // Invalid/unknown phenoId: plant proceeds normally with null bonuses (no hard block).
      const selectedPheno = phenoId ? state.phenotypes.find(p => p.id === phenoId) : null;

      const newBatch = {
        id: `batch-${++batchIdCounter}`,
        strainId,
        plantCount: seedCount,
        stage: 'seedling',
        dayInStage: 0,
        totalAge: 0,
        // Dirty room penalty: residual pathogens and contamination hurt early growth
        health: isDirty ? 80 : 95,
        stress: isDirty ? 20 : 5,
        stressHistory: [],
        yieldPotential: 100,
        thcAccumulation: 0,
        terpeneProfile: 50,
        visualAppeal: 80,
        hermaphroditeRisk: 0,
        moldRisk: 0,
        pestRisk: 0,
        projectedGrams: 0,
        harvestedGrams: null,
        qualityScore: null,
        selectedPhenoId: selectedPheno ? selectedPheno.id   : null,
        phenoBonuses:    selectedPheno ? selectedPheno.bonuses : null,
      };

      const plantNotif = isDirty
        ? `⚠️ Planted ${seedCount}x ${strainId} in uncleaned room — batch starts stressed. Send a worker to deep-clean first next time.`
        : `🌱 Planted ${seedCount}x ${strainId} in ${roomId}`;

      return {
        ...state,
        seedInventory: seedInv,
        rooms: state.rooms.map(room =>
          room.id === roomId ? { ...room, batch: newBatch, alerts: [], needsCleaning: false } : room
        ),
        notifications: [
          ...state.notifications,
          { id: `notif-plant-${Date.now()}`, message: plantNotif, type: isDirty ? 'warning' : 'info', day: state.gameDay, read: false }
        ],
      };
    }

    // ── HARVEST_BATCH ───────────────────────────────────────────────────────
    case 'HARVEST_BATCH': {
      const { roomId } = action.payload;
      const room = state.rooms.find(r => r.id === roomId);
      if (!room || !room.batch || room.batch.stage !== 'harvest') return state;

      const { getStrain } = require('./data/strains');
      const strain = getStrain(room.batch.strainId);
      const harvesters = state.workers.filter(w => w.assignedRoomId === roomId);
      const avgSkill = harvesters.length > 0
        ? harvesters.reduce((s, w) => s + w.skill, 0) / harvesters.length
        : 40;

      const grams = calculateFinalYield(room.batch, strain, avgSkill);
      const quality = calculateHarvestQuality(room.batch, strain);

      const inventoryItem = {
        id: `inv-${inventoryIdCounter++}`,
        batchId: room.batch.id,
        strainId: room.batch.strainId,
        grams,
        qualityScore: quality,
        // Snapshot batch potency/appeal data so reviewers can assess it later
        thcAccumulation: room.batch.thcAccumulation,
        terpeneProfile: room.batch.terpeneProfile,
        visualAppeal: room.batch.visualAppeal,
        harvestDay: state.gameDay,
        cureDay: null,
        status: 'fresh', // 'fresh' | 'cured' | 'sold'
        // Corporate backing stamp
        corporateBacked: state.corporateInvestmentAccepted && !state.corporateBoughtOut,
      };

      // Corporate directive tracking for quality harvests
      let harvestCorpDirective = state.corporateDirective;
      if (state.corporateInvestmentAccepted && !state.corporateBoughtOut
          && harvestCorpDirective?.type === 'quality_harvest'
          && quality >= 70) {
        harvestCorpDirective = { ...harvestCorpDirective, progress: harvestCorpDirective.progress + 1 };
      }

      // Quality rep gain
      const repGain = calculateRepGain(quality, 'wholesale', state.unlockedTech);

      // ── Pheno hunt trigger ──────────────────────────────────────────────────
      // Fires inline, same pattern as pendingReviews in SELL_HARVEST.
      let newPhenoCandidates = state.phenoCandidates;
      if (
        state.unlockedTech.includes('C1') &&
        quality >= 50 &&
        state.phenoCandidates === null
      ) {
        const labRoom = state.rooms.find(r => r.type === 'lab');
        const hasSequencer = labRoom?.equipment?.sequencer?.installed ?? false;
        const candidates = rollPhenoCandidates(room.batch.strainId, quality, hasSequencer);
        newPhenoCandidates = { strainId: room.batch.strainId, batchQuality: quality, candidates };
      }

      return {
        ...state,
        rooms: state.rooms.map(r =>
          r.id === roomId
            ? { ...r, batch: null, alerts: [], needsCleaning: true }
            : r
        ),
        inventory: [...state.inventory, inventoryItem],
        reputation: Math.min(100, state.reputation + repGain),
        qualityScore: Math.round((state.qualityScore * 0.8) + (quality * 0.2)), // rolling average
        corporateDirective: harvestCorpDirective,
        phenoCandidates: newPhenoCandidates,
        notifications: [
          ...state.notifications,
          { id: `notif-harvest-${Date.now()}`, message: `✂️ Harvested ${grams}g at quality ${quality}/100!`, type: 'success', day: state.gameDay, read: false }
        ],
      };
    }

    // ── SAVE_PHENO ──────────────────────────────────────────────────────────
    case 'SAVE_PHENO': {
      if (!state.phenoCandidates) return state;
      const { candidateIndex, name } = action.payload;
      const strainId = state.phenoCandidates.strainId;
      const trimmed = (name || '').trim();
      if (!trimmed || trimmed.length > 24) return state;
      const candidate = state.phenoCandidates.candidates[candidateIndex];
      if (!candidate) return state;

      // ID generation: Date.now().toString(36) — no new dependency needed, collision-safe for
      // single-player game where saves can't merge.
      const newPheno = {
        id: `pheno-${Date.now().toString(36)}`,
        strainId,
        name: trimmed,
        rarity: candidate.rarity,
        bonuses: candidate.bonuses,
        discoveredDay: state.gameDay,
      };

      return {
        ...state,
        phenotypes: [...state.phenotypes, newPheno],
        phenoCandidates: null,
        notifications: [
          ...state.notifications,
          { id: `notif-pheno-${Date.now()}`, message: `🧬 "${trimmed}" saved to pheno library!`, type: 'success', day: state.gameDay, read: false },
        ],
      };
    }

    // ── DISMISS_PHENO_CANDIDATES ─────────────────────────────────────────────
    case 'DISMISS_PHENO_CANDIDATES': {
      return { ...state, phenoCandidates: null };
    }

    // ── UNLOCK_ROOM ─────────────────────────────────────────────────────────
    case 'UNLOCK_ROOM': {
      const { roomId } = action.payload;
      const room = state.rooms.find(r => r.id === roomId);
      if (!room || room.unlocked) return state;
      const cost = room.unlockCost || 5000;
      if (state.cash < cost) return state;
      return {
        ...state,
        cash: state.cash - cost,
        totalExpenses: state.totalExpenses + cost,
        rooms: state.rooms.map(r => r.id === roomId ? { ...r, unlocked: true } : r),
        notifications: [
          ...state.notifications,
          { id: `notif-room-${Date.now()}`, message: `🏭 ${room.name} is now open!`, type: 'success', day: state.gameDay, read: false }
        ],
      };
    }

    // ── REPAIR_EQUIPMENT ────────────────────────────────────────────────────
    case 'REPAIR_EQUIPMENT': {
      const { roomId, equipType } = action.payload;
      const repairCost = 200;
      if (state.cash < repairCost) return state;
      return {
        ...state,
        cash: state.cash - repairCost,
        totalExpenses: state.totalExpenses + repairCost,
        rooms: state.rooms.map(room => {
          if (room.id !== roomId) return room;
          return {
            ...room,
            equipment: {
              ...room.equipment,
              [equipType]: { ...room.equipment[equipType], health: 100 },
            },
          };
        }),
      };
    }

    // ── ASSIGN_WORKER ───────────────────────────────────────────────────────
    case 'ASSIGN_WORKER': {
      const { workerId, roomId } = action.payload;
      return {
        ...state,
        workers: state.workers.map(w =>
          w.id === workerId ? { ...w, assignedRoomId: roomId } : w
        ),
        rooms: state.rooms.map(room => {
          const workers = room.assignedWorkers || [];
          if (room.id === roomId && !workers.includes(workerId)) {
            return { ...room, assignedWorkers: [...workers, workerId] };
          }
          if (room.id !== roomId && workers.includes(workerId)) {
            return { ...room, assignedWorkers: workers.filter(id => id !== workerId) };
          }
          return room;
        }),
      };
    }

    // ── UNASSIGN_WORKER ─────────────────────────────────────────────────────
    case 'UNASSIGN_WORKER': {
      const { workerId } = action.payload;
      return {
        ...state,
        workers: state.workers.map(w =>
          w.id === workerId ? { ...w, assignedRoomId: null } : w
        ),
        rooms: state.rooms.map(room => ({
          ...room,
          assignedWorkers: (room.assignedWorkers || []).filter(id => id !== workerId),
        })),
      };
    }

    // ── CONFIGURE_AUTOMATION ────────────────────────────────────────────────
    case 'CONFIGURE_AUTOMATION': {
      const { roomId: autoRoomId, managerId: autoMgrId, defaultStrainId: autoStrainId, defaultPlantCount: autoPlantCount, recipeId: autoRecipeId } = action.payload;
      return {
        ...state,
        rooms: state.rooms.map(room => {
          if (room.id !== autoRoomId) return room;
          const enabled = !!(autoMgrId && autoStrainId);
          return { ...room, automation: { enabled, managerId: autoMgrId, defaultStrainId: autoStrainId, defaultPlantCount: autoPlantCount ?? 10, recipeId: autoRecipeId ?? room.automation?.recipeId ?? null } };
        }),
        workers: state.workers.map(w => {
          if (w.id === autoMgrId) return { ...w, assignedRoomId: autoRoomId };
          if (w.assignedRoomId === autoRoomId && w.type === 'manager' && w.id !== autoMgrId)
            return { ...w, assignedRoomId: null };
          return w;
        }),
      };
    }

    // ── HIRE_WORKER ─────────────────────────────────────────────────────────
    case 'HIRE_WORKER': {
      const { workerData } = action.payload;
      const hiringCost = Math.round(workerData.wageDemand * 0.5); // one-time hiring fee
      if (state.cash < hiringCost) return state;
      return {
        ...state,
        cash: state.cash - hiringCost,
        totalExpenses: state.totalExpenses + hiringCost,
        workers: [...state.workers, { ...workerData, hiredOnDay: state.gameDay }],
        jobMarket: state.jobMarket.filter(w => w.id !== workerData.id),
        notifications: [
          ...state.notifications,
          { id: `notif-hire-${Date.now()}`, message: `👤 Hired ${workerData.name} (${workerData.type})`, type: 'info', day: state.gameDay, read: false }
        ],
      };
    }

    // ── FIRE_WORKER ─────────────────────────────────────────────────────────
    case 'FIRE_WORKER': {
      const { workerId } = action.payload;
      const worker = state.workers.find(w => w.id === workerId);
      if (!worker) return state;
      // Fire penalty — all other workers morale -5
      return {
        ...state,
        workers: state.workers
          .filter(w => w.id !== workerId)
          .map(w => ({ ...w, morale: Math.max(0, w.morale - 5) })),
        rooms: state.rooms.map(room => ({
          ...room,
          assignedWorkers: (room.assignedWorkers || []).filter(id => id !== workerId),
        })),
        notifications: [
          ...state.notifications,
          { id: `notif-fire-${Date.now()}`, message: `👋 ${worker.name} left the team. Staff morale dropped.`, type: 'warning', day: state.gameDay, read: false }
        ],
      };
    }

    // ── WORKER_TASK ─────────────────────────────────────────────────────────
    // Manual tasks a worker can perform in their assigned room
    case 'WORKER_TASK': {
      const { workerId, roomId, task } = action.payload;
      const worker = state.workers.find(w => w.id === workerId);
      const room = state.rooms.find(r => r.id === roomId);
      if (!worker || !room) return state;

      // ── deep_clean: post-harvest task, requires an EMPTY room ─────────────
      // Resets the full recipe to veg defaults so the next batch starts correctly.
      // Without this, flowering lightHours/humidity/EC carry over and hurt the next grow.
      if (task === 'deep_clean') {
        if (room.batch) return state; // can't clean while a batch is growing
        const VEG_DEFAULTS = {
          lightHours:  18,
          humidity:    60,
          temperature: 24,
          nutrientEC:  1.2,
          phTarget:    6.2,
          co2:         800,
        };
        return {
          ...state,
          rooms: state.rooms.map(r => r.id !== roomId ? r : {
            ...r,
            needsCleaning: false,
            recipe: { ...r.recipe, ...VEG_DEFAULTS },
          }),
          notifications: [...state.notifications, {
            id: `notif-clean-${Date.now()}`,
            message: `🧹 ${worker.name} deep-cleaned ${room.name} — recipe reset to veg defaults. Ready for next batch.`,
            type: 'success',
            day: state.gameDay,
            read: false,
          }],
        };
      }

      // All other tasks require an active batch
      if (!room.batch) return state;

      const effectiveness = (worker.skill / 100) * (worker.morale / 100);
      let updatedBatch = { ...room.batch };
      let notifMsg = null;

      switch (task) {
        case 'clear_mold': {
          // Trimmers aren't trained for IPM — Austin Terpz tries but doesn't do well
          const isTrimmer = worker.type === 'trimmer';
          const isAustin  = worker.id === 'worker-austin';
          if (isTrimmer && !isAustin) return state; // blocked
          const trimmerPenalty = isTrimmer ? 0.35 : 1.0; // Austin tries at 35% effectiveness
          const reduction = Math.round((15 + effectiveness * 30) * trimmerPenalty);
          updatedBatch.moldRisk = Math.max(0, updatedBatch.moldRisk - reduction);
          notifMsg = isTrimmer
            ? `🍄 ${worker.name} tried to clear mold... not great. Risk down to ${Math.round(updatedBatch.moldRisk)}% (send a grower next time)`
            : `🍄 ${worker.name} cleared mold — risk down to ${Math.round(updatedBatch.moldRisk)}%`;
          break;
        }
        case 'treat_pests': {
          const isTrimmer = worker.type === 'trimmer';
          const isAustin  = worker.id === 'worker-austin';
          if (isTrimmer && !isAustin) return state;
          const trimmerPenalty = isTrimmer ? 0.35 : 1.0;
          const reduction = Math.round((15 + effectiveness * 30) * trimmerPenalty);
          updatedBatch.pestRisk = Math.max(0, updatedBatch.pestRisk - reduction);
          notifMsg = isTrimmer
            ? `🐛 ${worker.name} fumbled the pest treatment... Risk down to ${Math.round(updatedBatch.pestRisk)}% (send a grower next time)`
            : `🐛 ${worker.name} treated pests — risk down to ${Math.round(updatedBatch.pestRisk)}%`;
          break;
        }
        case 'defoliate': {
          // Lower stress, larger health boost (increased effectiveness to reduce spamming)
          // Allow recipe to scale the effect (recipe.defoliateMultiplier)
          const recipeMultiplier = room.recipe?.defoliateMultiplier || 1.0;
          const stressReduction = Math.round((10 + effectiveness * 15) * recipeMultiplier);
          const healthBoost = Math.round((4 + effectiveness * 8) * recipeMultiplier);
          updatedBatch.stress   = Math.max(0,   updatedBatch.stress   - stressReduction);
          updatedBatch.health   = Math.min(100,  updatedBatch.health   + healthBoost);
          notifMsg = `✂️ ${worker.name} defoliated — plants breathing better (−${stressReduction} stress)`;
          break;
        }
        case 'flip_lights': {
          // Worker flips the light schedule to 12/12 for flowering
          // Recipe update is handled in the return below
          notifMsg = `🌙 ${worker.name} flipped the lights to 12/12 — herm risk will stop climbing`;
          break;
        }
        case 'move_plants': {
          // Transfer batch from this room to a destination flower room
          const { toRoomId } = action.payload;
          if (!toRoomId) return state;
          const destRoom = state.rooms.find(r => r.id === toRoomId);
          if (!destRoom || !destRoom.unlocked || destRoom.batch) return state;
          // Can't move a batch that's already at harvest or being cured
          if (room.batch.stage === 'harvest' || room.batch.stage === 'curing') return state;
          // Moving from veg/seedling triggers flowering; already-flowering batches keep their stage
          const triggersFlowering = room.batch.stage === 'vegetative' || room.batch.stage === 'seedling';
          const movedBatch = triggersFlowering
            ? { ...room.batch, stage: 'flowering', dayInStage: 0 }
            : { ...room.batch };
          const isFlowerRoom = destRoom.recipe?.lightHours <= 13;
          return {
            ...state,
            rooms: state.rooms.map(r => {
              if (r.id === roomId)   return { ...r, batch: null, needsCleaning: true, alerts: [] };
              if (r.id === toRoomId) return { ...r, batch: movedBatch };
              return r;
            }),
            notifications: [...state.notifications,
              { id: `notif-move-${Date.now()}`, message: isFlowerRoom
                  ? `🌸 ${worker.name} transferred plants to ${destRoom.name} — already on flower schedule, ideal conditions ahead.`
                  : `🌿 ${worker.name} moved plants to ${destRoom.name}. Set lights to 12h to start flowering.`,
                type: 'success', day: state.gameDay, read: false },
            ],
          };
        }
        default:
          return state;
      }

      return {
        ...state,
        rooms: state.rooms.map(r => {
          if (r.id !== roomId) return r;
          return {
            ...r,
            batch: updatedBatch,
            // flip_lights updates the room recipe directly
            recipe: task === 'flip_lights' ? { ...r.recipe, lightHours: 12 } : r.recipe,
          };
        }),
        notifications: notifMsg ? [
          ...state.notifications,
          { id: `notif-task-${Date.now()}`, message: notifMsg, type: 'success', day: state.gameDay, read: false }
        ] : state.notifications,
      };
    }

    // ── FIX_EVENT ────────────────────────────────────────────────────────────
    // Workers resolve active events. Skill determines which events can be handled.
    // More expensive/skilled workers unlock higher-tier event resolution.
    case 'FIX_EVENT': {
      const { workerId: fixWorkerId, eventId } = action.payload;
      const fixWorker = state.workers.find(w => w.id === fixWorkerId);
      if (!fixWorker) return state;
      const eventToFix = state.activeEvents.find(e => e.id === eventId && !e.resolved);
      if (!eventToFix) return state;

      // Min skill required to handle each event type.
      // Basic plant workers (skill 30-50) handle plant problems.
      // Technical staff (skill 55-70) handle infrastructure.
      // Compliance experts (skill 70+) handle inspections.
      const FIX_TIERS = {
        heat_wave:             20,
        scammer:               20,
        pest_outbreak:         30,
        powdery_mildew:        35,
        power_outage:          50,
        equipment_failure:     55,
        regulatory_inspection: 70,
      };
      const requiredSkill = FIX_TIERS[eventToFix.type] || 50;
      if (fixWorker.skill < requiredSkill) {
        return {
          ...state,
          notifications: [...state.notifications,
            { id: `notif-fix-fail-${Date.now()}`, message: `❌ ${fixWorker.name} (skill ${fixWorker.skill}) can't handle this — needs skill ≥${requiredSkill}.`, type: 'warning', day: state.gameDay, read: false },
          ],
        };
      }

      // Skilled workers negotiate better material costs (up to 35% discount)
      const baseCost = eventToFix.resolutionCost || 0;
      const discount = Math.floor((fixWorker.skill / 100) * 0.35 * baseCost);
      const actualCost = Math.max(0, baseCost - discount);
      if (actualCost > 0 && state.cash < actualCost) return state;

      // Regulatory inspection: skilled workers reduce fines + can prevent shutdown
      if (eventToFix.type === 'regulatory_inspection') {
        const { resolveInspection } = require('./systems/events');
        const outcome = resolveInspection(state);
        const fineReduction = Math.min(0.7, (fixWorker.skill - 75) / 100);
        const reducedFine = Math.round(outcome.totalFines * (1 - fineReduction));
        const canPreventShutdown = fixWorker.skill >= 90;
        return {
          ...state,
          cash: state.cash - reducedFine,
          totalExpenses: state.totalExpenses + reducedFine,
          reputation: Math.max(0, state.reputation - Math.round(outcome.repLoss * (1 - fineReduction))),
          complianceScore: Math.max(0, state.complianceScore - 5),
          isShutDown: state.isShutDown || (outcome.isShutdown && !canPreventShutdown),
          activeEvents: state.activeEvents.map(e => e.id === eventId ? { ...e, resolved: true, resolvedDay: state.gameDay } : e),
          notifications: [...state.notifications,
            { id: `notif-fix-inspect-${Date.now()}`, message: `📋 ${fixWorker.name} handled the inspection — Fine: $${reducedFine.toLocaleString()}${canPreventShutdown ? '. No shutdown risk.' : ''}.`, type: outcome.violations.length > 0 ? 'warning' : 'success', day: state.gameDay, read: false },
          ],
        };
      }

      return {
        ...state,
        cash: state.cash - actualCost,
        totalExpenses: state.totalExpenses + actualCost,
        activeEvents: state.activeEvents.map(e => e.id === eventId ? { ...e, resolved: true, resolvedDay: state.gameDay } : e),
        notifications: [...state.notifications,
          { id: `notif-fix-${Date.now()}`, message: `🔧 ${fixWorker.name} resolved: ${eventToFix.label}${actualCost > 0 ? ` — Cost: $${actualCost.toLocaleString()}` : ''}.`, type: 'success', day: state.gameDay, read: false },
        ],
      };
    }

    // ── ACCEPT_CORPORATE_INVESTMENT ──────────────────────────────────────────
    // Stage 2 unlock: $1M corporate investment in exchange for 25% equity.
    // Unlocks all facility rooms and enables scientist hiring.
    case 'ACCEPT_CORPORATE_INVESTMENT': {
      if (!state.corporateOfferAvailable) return state;
      return {
        ...state,
        cash: state.cash + 1000000,
        corporateOfferAvailable: false,
        corporateInvestmentAccepted: true,
        // Unlock all remaining rooms
        rooms: state.rooms.map(r => ({ ...r, unlocked: true })),
        notifications: [...state.notifications,
          { id: `notif-corp-${Date.now()}`, message: '💼 $1,000,000 received from CannCorp Inc.! All facilities unlocked. Hire scientists to accelerate research.', type: 'success', day: state.gameDay, read: false },
        ],
      };
    }

    // ── DECLINE_CORPORATE_INVESTMENT ────────────────────────────────────────
    case 'DECLINE_CORPORATE_INVESTMENT': {
      if (!state.corporateOfferAvailable) return state;
      return {
        ...state,
        corporateOfferAvailable: false,
        notifications: [...state.notifications,
          { id: `notif-corp-decline-${Date.now()}`, message: '💼 You declined CannCorp Inc.\'s offer. You\'ll grow on your own terms.', type: 'info', day: state.gameDay, read: false },
        ],
      };
    }

    // ── SELL_HARVEST ────────────────────────────────────────────────────────
    case 'SELL_HARVEST': {
      const { inventoryId, channel, quantity } = action.payload;
      const item = state.inventory.find(i => i.id === inventoryId);
      if (!item || item.status === 'sold' || item.status === 'in_curing' || item.status === 'in_processing') return state;

      // Corporate channel restriction check
      if (state.corporateInvestmentAccepted && !state.corporateBoughtOut
          && state.corporateChannelRestriction
          && channel === state.corporateChannelRestriction.channel
          && state.gameDay < state.corporateChannelRestriction.until) {
        return {
          ...state,
          notifications: [...state.notifications, {
            id: `notif-corp-restrict-${Date.now()}`,
            message: `🚫 CannCorp has restricted ${channel} sales.`,
            type: 'warning', day: state.gameDay, read: false,
          }],
        };
      }

      const sellQty = Math.min(quantity || item.grams, item.grams);
      const demandMult = Math.max(0.85, Math.min(1.30, state.demandModifier ?? 1.0));
      const rawSale = calculateHarvestValue(
        { ...item, grams: sellQty }, channel, state.reputation, state.marketPrices, state.supplyGlut, state.unlockedTech
      );
      const pricePerGram = Math.round(rawSale.pricePerGram * demandMult * 100) / 100;
      const totalValue = Math.round(rawSale.totalValue * demandMult);
      if (totalValue <= 0) {
        return {
          ...state,
          notifications: [...state.notifications, {
            id: `notif-sell-err-${Date.now()}`,
            message: `⚠️ Could not price this batch — strain data missing. Try starting a new game or contact support.`,
            type: 'warning', day: state.gameDay, read: false,
          }],
        };
      }

      const repGain = calculateRepGain(item.qualityScore, channel, state.unlockedTech);
      const illegalIncrement = channel === 'blackmarket' ? 1 : 0;
      const updatedItem = { ...item, grams: item.grams - sellQty, status: item.grams - sellQty <= 0 ? 'sold' : item.status };

      // Black market: accumulate risk and potentially trigger a compliance investigation
      const newIllegalCount = state.illegalSalesCount + illegalIncrement;
      const investigationRisk = newIllegalCount * 0.05; // +5% per sale
      const investigationTriggered = illegalIncrement > 0 && Math.random() < investigationRisk;
      const complianceInvestigationEvents = investigationTriggered ? [{
        id: `event-compliance-${Date.now()}`,
        type: 'compliance_investigation',
        label: 'Compliance Investigation',
        icon: '🚨',
        description: `Authorities have opened an investigation after ${newIllegalCount} unlicensed sale(s).`,
        severity: 'critical',
        resolutionCost: 0,
        startDay: state.gameDay,
        resolved: false,
        resolvedDay: null,
      }] : [];

      // Illegal sale notification
      const illegalNotif = illegalIncrement > 0 ? [{
        id: `notif-illegal-${Date.now()}`,
        message: investigationTriggered
          ? '🚨 Illegal sale triggered a compliance investigation!'
          : `⚠️ Illegal sale complete. Investigation risk: ${Math.min(100, Math.round(investigationRisk * 100))}%`,
        type: investigationTriggered ? 'danger' : 'warning',
        day: state.gameDay,
        read: false,
      }] : [];

      // Queue a reviewer — wholesale/medical/retail trigger reviews; blackmarket never does
      const newPendingReviews = [...(state.pendingReviews || [])];
      const isExtract = (item.type ?? 'flower') === 'extract';
      if (!isExtract && channel !== 'blackmarket' && sellQty >= 50) {
        const available = getAvailableReviewers(state.stage);
        if (available.length > 0) {
          // 45% chance any qualifying sale gets reviewed
          if (Math.random() < 0.45) {
            const reviewer = available[Math.floor(Math.random() * available.length)];
            newPendingReviews.push({
              id: `rev-${Date.now()}`,
              reviewerId: reviewer.id,
              reviewerName: reviewer.name,
              reviewerHandle: reviewer.handle,
              reviewerEmoji: reviewer.emoji,
              inventorySnapshot: { ...item, grams: sellQty },
              publishDay: state.gameDay + 7,
              channel,
            });
          }
        }
      }

      // Corporate directive tracking on sale
      let sellCorpDirective = state.corporateDirective;
      let sellCorpSatisfaction = state.corporateSatisfaction;
      if (state.corporateInvestmentAccepted && !state.corporateBoughtOut && sellCorpDirective) {
        if (sellCorpDirective.type === 'revenue_target') {
          sellCorpDirective = { ...sellCorpDirective, progress: sellCorpDirective.progress + totalValue };
        }
        if (sellCorpDirective.type === 'no_blackmarket' && channel === 'blackmarket') {
          sellCorpDirective = { ...sellCorpDirective, progress: -1 };
        }
      }
      if (state.corporateInvestmentAccepted && !state.corporateBoughtOut && channel === 'blackmarket') {
        sellCorpSatisfaction = Math.max(0, sellCorpSatisfaction - 5);
      }

      return {
        ...state,
        cash: state.cash + totalValue,
        totalRevenue: state.totalRevenue + totalValue,
        reputation: Math.max(0, Math.min(100, state.reputation + repGain)),
        // Reset count if investigation triggered so risk resets after each catch
        illegalSalesCount: investigationTriggered ? 0 : newIllegalCount,
        activeEvents: [...state.activeEvents, ...complianceInvestigationEvents],
        inventory: state.inventory.map(i => i.id === inventoryId ? updatedItem : i),
        pendingReviews: newPendingReviews,
        corporateDirective: sellCorpDirective,
        corporateSatisfaction: sellCorpSatisfaction,
        notifications: [
          ...state.notifications,
          { id: `notif-sell-${Date.now()}`, message: `💵 Sold ${sellQty}g @ $${pricePerGram.toFixed(2)}/g = $${totalValue.toLocaleString()}`, type: 'success', day: state.gameDay, read: false },
          ...illegalNotif,
        ],
      };
    }

    // ── ACCEPT_CONTRACT ─────────────────────────────────────────────────────
    case 'ACCEPT_CONTRACT': {
      const { contractId } = action.payload;
      const contract = state.availableContracts.find(c => c.id === contractId);
      if (!contract) return state;
      const accepted = { ...contract, status: 'active', acceptedDay: state.gameDay, deadline: state.gameDay + contract.durationDays };
      return {
        ...state,
        availableContracts: state.availableContracts.filter(c => c.id !== contractId),
        activeContracts: [...state.activeContracts, accepted],
        notifications: [
          ...state.notifications,
          { id: `notif-contract-${Date.now()}`, message: `📋 Contract accepted: ${contract.label} — ${contract.gramsRequired}g due in ${contract.durationDays} days`, type: 'info', day: state.gameDay, read: false }
        ],
      };
    }

    // ── FULFILL_CONTRACT ────────────────────────────────────────────────────
    case 'FULFILL_CONTRACT': {
      const { contractId, inventoryId, grams } = action.payload;
      const contract = state.activeContracts.find(c => c.id === contractId);
      const item = state.inventory.find(i => i.id === inventoryId);
      if (!contract || !item) return state;
      if (item.status === 'sold' || item.status === 'in_curing' || item.status === 'in_processing') return state;
      if (item.qualityScore < contract.qualityFloor) return state; // quality too low

      const alreadyDelivered = contract.gramsDelivered || 0;
      const deliverGrams = Math.min(grams, item.grams, contract.gramsRequired - alreadyDelivered);
      if (!deliverGrams || deliverGrams <= 0) return state;
      const contractDemandMult = Math.max(0.85, Math.min(1.30, state.demandModifier ?? 1.0));
      const rawContractSale = calculateHarvestValue(
        { ...item, grams: deliverGrams },
        contract.channel,
        state.reputation,
        state.marketPrices,
        state.supplyGlut,
        state.unlockedTech
      );
      const totalValue = Math.round(rawContractSale.totalValue * contractDemandMult);
      if (totalValue <= 0) {
        return {
          ...state,
          notifications: [...state.notifications, {
            id: `notif-contract-err-${Date.now()}`,
            message: `⚠️ Could not price this batch — strain data missing. Try starting a new game or contact support.`,
            type: 'warning', day: state.gameDay, read: false,
          }],
        };
      }
      const adjustedValue = Math.round(totalValue * contract.priceMultiplier);
      const updatedContract = {
        ...contract,
        gramsDelivered: alreadyDelivered + deliverGrams,
        status: alreadyDelivered + deliverGrams >= contract.gramsRequired ? 'fulfilled' : 'active',
      };
      const updatedItem = { ...item, grams: item.grams - deliverGrams, status: item.grams - deliverGrams <= 0 ? 'sold' : item.status };

      const repGain = updatedContract.status === 'fulfilled' ? contract.repReward : 0;

      // Corporate directive tracking for contract fulfillment
      let fulfillCorpDirective = state.corporateDirective;
      if (state.corporateInvestmentAccepted && !state.corporateBoughtOut
          && fulfillCorpDirective?.type === 'fulfill_contracts'
          && updatedContract.status === 'fulfilled') {
        fulfillCorpDirective = { ...fulfillCorpDirective, progress: fulfillCorpDirective.progress + 1 };
      }

      return {
        ...state,
        cash: state.cash + adjustedValue,
        totalRevenue: state.totalRevenue + adjustedValue,
        reputation: Math.min(100, state.reputation + repGain),
        reliabilityScore: Math.min(100, state.reliabilityScore + (repGain > 0 ? 3 : 0)),
        activeContracts: state.activeContracts.map(c => c.id === contractId ? updatedContract : c),
        contractHistory: updatedContract.status === 'fulfilled'
          ? [...state.contractHistory, updatedContract]
          : state.contractHistory,
        inventory: state.inventory.map(i => i.id === inventoryId ? updatedItem : i),
        corporateDirective: fulfillCorpDirective,
        notifications: [
          ...state.notifications,
          { id: `notif-fulfill-${Date.now()}`, message: `✅ Delivered ${deliverGrams}g to ${contract.label} — $${adjustedValue.toLocaleString()}`, type: 'success', day: state.gameDay, read: false }
        ],
      };
    }

    // ── BUY_LICENSE ─────────────────────────────────────────────────────────
    case 'BUY_LICENSE': {
      const { tier } = action.payload;
      const cost = LICENSE_COSTS[tier];
      if (!cost || state.cash < cost || tier <= state.licenseLevel) return state;
      return {
        ...state,
        cash: state.cash - cost,
        totalExpenses: state.totalExpenses + cost,
        licenseLevel: tier,
        complianceScore: Math.min(100, state.complianceScore + 10),
        notifications: [
          ...state.notifications,
          { id: `notif-license-${Date.now()}`, message: `📜 License acquired: Tier ${tier}!`, type: 'success', day: state.gameDay, read: false }
        ],
      };
    }

    // ── PAY_FINE ────────────────────────────────────────────────────────────
    case 'PAY_FINE': {
      const { violationId } = action.payload;
      const violation = state.violations.find(v => v.id === violationId);
      if (!violation || violation.paid) return state;
      const fineData = VIOLATION_FINES[violation.type] || VIOLATION_FINES.minor;
      if (state.cash < fineData.amount) return state;
      return {
        ...state,
        cash: state.cash - fineData.amount,
        totalExpenses: state.totalExpenses + fineData.amount,
        violations: state.violations.map(v => v.id === violationId ? { ...v, paid: true } : v),
        isShutDown: violation.type === 'critical' ? false : state.isShutDown,
      };
    }

    // ── START_RESEARCH ──────────────────────────────────────────────────────
    case 'START_RESEARCH': {
      const { nodeId } = action.payload;
      const { getTechNode } = require('./data/technology');
      const node = getTechNode(nodeId);
      if (!node || state.activeResearch || state.unlockedTech.includes(nodeId)) return state;
      if (state.cash < node.cost) return state;
      return {
        ...state,
        cash: state.cash - node.cost,
        totalExpenses: state.totalExpenses + node.cost,
        activeResearch: {
          nodeId,
          pointsRequired: node.rp,
          pointsAccumulated: 0,
          startDay: state.gameDay,
        },
        notifications: [
          ...state.notifications,
          { id: `notif-research-${Date.now()}`, message: `🔬 Started research: ${node.name}`, type: 'info', day: state.gameDay, read: false }
        ],
      };
    }

    // ── UNLOCK_STRAIN ───────────────────────────────────────────────────────
    case 'UNLOCK_STRAIN': {
      const { strainId } = action.payload;
      const { getStrain } = require('./data/strains');
      const strain = getStrain(strainId);
      if (!strain || state.unlockedStrains.includes(strainId)) return state;
      if (strain.unlockCostType === 'cash' && state.cash < strain.unlockCost) return state;
      if (strain.unlockCostType === 'rp' && state.researchPoints < strain.unlockCost) return state;
      return {
        ...state,
        cash: strain.unlockCostType === 'cash' ? state.cash - strain.unlockCost : state.cash,
        researchPoints: strain.unlockCostType === 'rp' ? state.researchPoints - strain.unlockCost : state.researchPoints,
        unlockedStrains: [...state.unlockedStrains, strainId],
        seedInventory: { ...state.seedInventory, [strainId]: (state.seedInventory[strainId] || 0) + 12 },
        notifications: [
          ...state.notifications,
          { id: `notif-strain-${Date.now()}`, message: `🧬 Unlocked strain: ${strain.name}!`, type: 'success', day: state.gameDay, read: false }
        ],
      };
    }

    // ── BUY_SEEDS ───────────────────────────────────────────────────────────
    case 'BUY_SEEDS': {
      const { strainId, quantity } = action.payload;
      const { getStrain } = require('./data/strains');
      const strain = getStrain(strainId);
      if (!strain || !state.unlockedStrains.includes(strainId)) return state;
      const totalCost = strain.seedCost * quantity;
      if (state.cash < totalCost) return state;
      return {
        ...state,
        cash: state.cash - totalCost,
        seedInventory: { ...state.seedInventory, [strainId]: (state.seedInventory[strainId] || 0) + quantity },
      };
    }

    // ── RESOLVE_EVENT ───────────────────────────────────────────────────────
    case 'RESOLVE_EVENT': {
      const { eventId } = action.payload;
      const event = state.activeEvents.find(e => e.id === eventId);
      if (!event) return state;
      const cost = event.resolutionCost || 0;
      if (cost > 0 && state.cash < cost) return state;

      // Regulatory inspections apply fines + rep loss + possible shutdown on resolution
      if (event.type === 'regulatory_inspection') {
        const { resolveInspection } = require('./systems/events');
        const { violations, totalFines, isShutdown, repLoss } = resolveInspection(state);
        const willShutdown = !state.isShutDown && isShutdown;
        const fineNotif = violations.length > 0
          ? [{ id: `notif-inspect-${Date.now()}`, type: isShutdown ? 'danger' : 'warning', day: state.gameDay, read: false,
               message: `📋 Inspection result: ${violations.map(v => v.reason).join('; ')} — Fine: $${totalFines.toLocaleString()}` }]
          : [{ id: `notif-inspect-${Date.now()}`, type: 'success', day: state.gameDay, read: false,
               message: '📋 Inspection passed! No violations found.' }];
        return {
          ...state,
          cash: state.cash - totalFines,
          totalExpenses: state.totalExpenses + totalFines,
          reputation: Math.max(0, state.reputation - repLoss),
          complianceScore: Math.max(0, state.complianceScore - repLoss),
          isShutDown: state.isShutDown || isShutdown,
          shutdownReason: willShutdown
            ? violations.find(v => v.type === 'critical')?.reason || 'Critical compliance violation'
            : state.shutdownReason,
          activeEvents: state.activeEvents.map(e => e.id === eventId ? { ...e, resolved: true, resolvedDay: state.gameDay } : e),
          notifications: [...state.notifications, ...fineNotif].slice(-50),
        };
      }

      // Equipment failure: maintenance worker reduces labor cost; restore HVAC health
      if (event.type === 'equipment_failure') {
        const hasMaintenance = state.workers.some(
          w => w.type === 'maintenance' && w.assignedRoomId === event.roomId
        );
        const issue = event.issue || {};
        const partsCost = issue.requiresParts ? (issue.partsCost || 0) : 0;
        const laborCost = hasMaintenance ? 0 : 100;
        const totalCost = partsCost + laborCost;
        if (totalCost > 0 && state.cash < totalCost) return state;
        return {
          ...state,
          cash: state.cash - totalCost,
          totalExpenses: state.totalExpenses + totalCost,
          rooms: state.rooms.map(r =>
            r.id === event.roomId
              ? { ...r, equipment: { ...r.equipment, hvac: { ...r.equipment.hvac, health: 100 } } }
              : r
          ),
          activeEvents: state.activeEvents.map(e =>
            e.id === eventId ? { ...e, resolved: true, resolvedDay: state.gameDay } : e
          ),
          notifications: [...state.notifications, {
            id: `notif-repair-${Date.now()}`,
            type: 'success',
            day: state.gameDay,
            read: false,
            message: hasMaintenance
              ? `🔧 Maintenance fixed ${event.roomName} — Parts: $${partsCost}`
              : `🔧 Equipment repaired in ${event.roomName} — Service call: $${totalCost}`,
          }].slice(-50),
        };
      }

      // Power outage: restore power + turn lights back on in the affected room
      if (event.type === 'power_outage') {
        return {
          ...state,
          cash: state.cash - cost,
          totalExpenses: state.totalExpenses + cost,
          activeEvents: state.activeEvents.map(e => e.id === eventId ? { ...e, resolved: true, resolvedDay: state.gameDay } : e),
          rooms: state.rooms.map(r => {
            if (r.id !== event.roomId) return r;
            return {
              ...r,
              poweredDown: false,
              environment: {
                ...r.environment,
                // Restore lights if they were installed before the outage
                lightOn: r.equipment?.lights?.installed ? true : r.environment.lightOn,
              },
            };
          }),
          notifications: [...state.notifications, {
            id: `notif-power-${Date.now()}`,
            type: 'success',
            day: state.gameDay,
            read: false,
            message: `⚡ Power restored in ${event.roomName || 'the room'}!`,
          }].slice(-50),
        };
      }

      // Room inspection: pass/fail based on complianceScore
      if (event.type === 'room_inspection') {
        const room = state.rooms.find(r => r.id === event.roomId);
        const { passed, closeDays, fine } = resolveRoomInspection(room, state);
        return {
          ...state,
          cash: state.cash - fine,
          totalExpenses: state.totalExpenses + fine,
          complianceScore: Math.max(0, Math.min(100, state.complianceScore + (passed ? 2 : -5))),
          rooms: state.rooms.map(r =>
            r.id === event.roomId
              ? { ...r, closedUntilDay: passed ? r.closedUntilDay : state.gameDay + closeDays }
              : r
          ),
          activeEvents: state.activeEvents.map(e => e.id === eventId ? { ...e, resolved: true, resolvedDay: state.gameDay } : e),
          notifications: [...state.notifications, {
            id: `notif-room-inspect-${Date.now()}`,
            type: passed ? 'success' : 'danger',
            day: state.gameDay,
            read: false,
            message: passed
              ? `🔍 ${room?.name || 'Room'} passed inspection! Compliance +2.`
              : `🔍 ${room?.name || 'Room'} FAILED inspection — closed for ${closeDays} days. Fine: $${fine.toLocaleString()}`,
          }].slice(-50),
        };
      }

      // Compliance investigation (triggered by repeated black market sales)
      if (event.type === 'compliance_investigation') {
        const { resolveInspection: resolveInsp } = require('./systems/events');
        const { violations, totalFines, repLoss } = resolveInsp(state);
        const failed = violations.length > 0;
        const newOffenseCount = state.complianceOffenseCount + (failed ? 1 : 0);
        const closeDaysMap = [3, 7, 14];
        const closeDays = closeDaysMap[Math.min(newOffenseCount - 1, 2)] || 14;
        return {
          ...state,
          cash: state.cash - totalFines,
          totalExpenses: state.totalExpenses + totalFines,
          reputation: Math.max(0, state.reputation - repLoss),
          complianceScore: Math.max(0, state.complianceScore - (failed ? 20 : 5)),
          illegalSalesCount: 0,
          complianceOffenseCount: newOffenseCount,
          rooms: failed
            ? state.rooms.map(r => r.unlocked ? { ...r, closedUntilDay: state.gameDay + closeDays } : r)
            : state.rooms,
          activeEvents: state.activeEvents.map(e => e.id === eventId ? { ...e, resolved: true, resolvedDay: state.gameDay } : e),
          notifications: [...state.notifications, {
            id: `notif-compliance-${Date.now()}`,
            type: failed ? 'danger' : 'warning',
            day: state.gameDay,
            read: false,
            message: failed
              ? `🚨 Investigation failed (offense #${newOffenseCount}) — all rooms closed for ${closeDays} days. Fine: $${totalFines.toLocaleString()}`
              : `📋 Investigation completed — minor violations only. Fine: $${totalFines.toLocaleString()}`,
          }].slice(-50),
        };
      }

      // Lab contamination: set back research points
      if (event.type === 'lab_contamination' && state.activeResearch) {
        return {
          ...state,
          activeResearch: {
            ...state.activeResearch,
            pointsAccumulated: Math.max(0, state.activeResearch.pointsAccumulated - 15),
          },
          activeEvents: state.activeEvents.map(e => e.id === eventId ? { ...e, resolved: true, resolvedDay: state.gameDay } : e),
          notifications: [...state.notifications, {
            id: `notif-lab-contam-${Date.now()}`,
            type: 'warning', day: state.gameDay, read: false,
            message: '🧫 Research contamination resolved. Lost 15 RP progress.',
          }].slice(-50),
        };
      }

      // Lab equipment failure: deduct cash (resolutionCost) and mark resolved
      if (event.type === 'lab_equipment_failure') {
        if (cost > 0 && state.cash < cost) return state;
        return {
          ...state,
          cash: state.cash - cost,
          totalExpenses: state.totalExpenses + cost,
          activeEvents: state.activeEvents.map(e => e.id === eventId ? { ...e, resolved: true, resolvedDay: state.gameDay } : e),
          notifications: [...state.notifications, {
            id: `notif-lab-repair-${Date.now()}`,
            type: 'success', day: state.gameDay, read: false,
            message: `🔬 Lab equipment repaired${cost > 0 ? ` — $${cost} service cost` : ''}.`,
          }].slice(-50),
        };
      }

      return {
        ...state,
        cash: state.cash - cost,
        totalExpenses: state.totalExpenses + cost,
        activeEvents: state.activeEvents.map(e => e.id === eventId ? { ...e, resolved: true, resolvedDay: state.gameDay } : e),
      };
    }

    // ── ACCEPT_BUYOUT ────────────────────────────────────────────────────────
    case 'ACCEPT_BUYOUT': {
      return {
        ...state,
        cash: state.cash + 1000000,
        totalRevenue: state.totalRevenue + 1000000,
        activeEvents: state.activeEvents.map(e =>
          e.type === 'buyout_offer' ? { ...e, resolved: true, resolvedDay: state.gameDay } : e
        ),
        notifications: [...state.notifications, {
          id: `notif-buyout-accept-${Date.now()}`,
          type: 'success',
          day: state.gameDay,
          read: false,
          message: '🎉 Congratulations! CannaVentures Capital has acquired your operation for $1,000,000. Keep building — the industry is yours.',
        }].slice(-50),
      };
    }

    // ── REJECT_BUYOUT ─────────────────────────────────────────────────────────
    case 'REJECT_BUYOUT': {
      return {
        ...state,
        activeEvents: state.activeEvents.map(e =>
          e.type === 'buyout_offer' ? { ...e, resolved: true, resolvedDay: state.gameDay } : e
        ),
        notifications: [...state.notifications, {
          id: `notif-buyout-reject-${Date.now()}`,
          type: 'milestone',
          day: state.gameDay,
          read: false,
          message: '💪 Offer declined. You\'re not selling — you\'re building an empire.',
        }].slice(-50),
      };
    }

    // ── TRANSFER_BATCH ───────────────────────────────────────────────────────
    case 'TRANSFER_BATCH': {
      const { fromRoomId, toRoomId } = action.payload;
      const fromRoom = state.rooms.find(r => r.id === fromRoomId);
      const toRoom   = state.rooms.find(r => r.id === toRoomId);
      if (!fromRoom?.batch || !toRoom?.unlocked || toRoom?.batch) return state;
      if (['harvest', 'curing'].includes(fromRoom.batch.stage)) return state;
      const triggersFlowering = ['seedling', 'vegetative'].includes(fromRoom.batch.stage);
      const movedBatch = triggersFlowering
        ? { ...fromRoom.batch, stage: 'flowering', dayInStage: 0 }
        : { ...fromRoom.batch };
      return {
        ...state,
        rooms: state.rooms.map(r => {
          if (r.id === fromRoomId) return { ...r, batch: null, needsCleaning: true, alerts: [] };
          if (r.id === toRoomId)   return { ...r, batch: movedBatch };
          return r;
        }),
        notifications: [...state.notifications, {
          id: `notif-transfer-${Date.now()}`,
          type: 'info',
          day: state.gameDay,
          read: false,
          message: `🌿 Batch transferred to ${toRoom.name}${triggersFlowering ? ' — Flowering triggered!' : ''}`,
        }].slice(-50),
      };
    }

    // ── SET_SELECTED_FACILITY_ROOM ───────────────────────────────────────────
    case 'SET_SELECTED_FACILITY_ROOM':
      return { ...state, ui: { ...state.ui, selectedFacilityRoomId: action.payload.roomId, drawerOpen: !!action.payload.roomId } };

    // ── CLOSE_FACILITY_DRAWER ────────────────────────────────────────────────
    case 'CLOSE_FACILITY_DRAWER':
      return { ...state, ui: { ...state.ui, drawerOpen: false, selectedFacilityRoomId: null } };

    // ── SET_FACILITY_PAGE ────────────────────────────────────────────────────
    case 'SET_FACILITY_PAGE':
      return { ...state, ui: { ...state.ui, facilityPage: action.payload } };

    // ── NAVIGATE ─────────────────────────────────────────────────────────────
    case 'NAVIGATE': {
      return {
        ...state,
        ui: {
          ...state.ui,
          activeScreen: action.payload.screen,
          selectedRoomId: action.payload.roomId || state.ui.selectedRoomId,
          modalOpen: null,
        },
      };
    }

    // ── OPEN_MODAL / CLOSE_MODAL ─────────────────────────────────────────────
    case 'OPEN_MODAL':
      return { ...state, ui: { ...state.ui, modalOpen: action.payload.modal } };
    case 'CLOSE_MODAL':
      return { ...state, ui: { ...state.ui, modalOpen: null } };

    // ── DISMISS_NOTIFICATION ─────────────────────────────────────────────────
    case 'DISMISS_NOTIFICATION': {
      const { id } = action.payload;
      return {
        ...state,
        notifications: state.notifications.map(n => n.id === id ? { ...n, read: true } : n),
      };
    }

    // ── DISMISS_ALL_NOTIFICATIONS ────────────────────────────────────────────
    case 'DISMISS_ALL_NOTIFICATIONS':
      return { ...state, notifications: state.notifications.map(n => ({ ...n, read: true })) };

    // ── TOGGLE_PAUSE ─────────────────────────────────────────────────────────
    case 'TOGGLE_PAUSE':
      return { ...state, paused: !state.paused };

    // ── SET_GAME_SPEED ────────────────────────────────────────────────────────
    case 'SET_GAME_SPEED':
      return { ...state, gameSpeed: action.payload.speed };

    // ── LOAD_GAME ─────────────────────────────────────────────────────────────
    case 'LOAD_GAME': {
      const { initialGameState: defaults } = require('./initialState');
      const saved = action.payload.savedState;
      // Migrate: backfill any keys added after a save was created so undefined never crashes the sim
      let loaded = {
        ...defaults,          // base defaults for any missing keys
        ...saved,             // player's saved data takes priority
        paused: true,
        // Explicit backfills for keys added after initial release
        pendingReviews:              saved.pendingReviews              ?? [],
        publishedReviews:            saved.publishedReviews            ?? [],
        activeEvents:                saved.activeEvents                ?? [],
        corporateOfferAvailable:     saved.corporateOfferAvailable     ?? false,
        corporateInvestmentAccepted: saved.corporateInvestmentAccepted ?? false,
        corporateBoughtOut:          saved.corporateBoughtOut          ?? false,
        corporateSatisfaction:       saved.corporateSatisfaction       ?? 75,
        corporateDirective:          saved.corporateDirective          ?? null,
        corporateDirectiveHistory:   saved.corporateDirectiveHistory   ?? [],
        corporateDirectiveMissCount: saved.corporateDirectiveMissCount ?? 0,
        corporateChannelRestriction: saved.corporateChannelRestriction ?? null,
        violations:       saved.violations       ?? [],
        shutdownReason:   saved.shutdownReason   ?? null,
        contractHistory:  saved.contractHistory  ?? [],
        phenotypes:       saved.phenotypes       ?? [],
        phenoCandidates:  null,                   // always reset on load — never restore a pending decision
        purchasedRecipes: saved.purchasedRecipes ?? [],
        cashHistory:      saved.cashHistory      ?? [{ week: 0, balance: saved.cash ?? defaults.cash }],
        priceHistory:     saved.priceHistory     ?? [],
        // Ensure every room has the new fields
        rooms: (saved.rooms ?? defaults.rooms).map(room => ({
          ...room,
          assignedWorkers: room.assignedWorkers ?? [],
          alerts:          room.alerts          ?? [],
          needsCleaning:   room.needsCleaning   ?? false,
          poweredDown:     room.poweredDown     ?? false,
          closedUntilDay:  room.closedUntilDay  ?? null,
          // Department classification (backfill for saves before department system)
          department: room.department
            ?? (room.type === 'curing' || room.type === 'processing' ? 'post-production'
              : room.type === 'lab' ? 'research' : 'production'),
          // Curing/processing room fields (added with post-harvest system)
          ...(room.type === 'curing'     ? { curingSlots:     room.curingSlots     ?? [] } : {}),
          ...(room.type === 'processing' ? { processingQueue: room.processingQueue ?? [] } : {}),
          recipe: {
            ...room.recipe,
            defoliateMultiplier: room.recipe?.defoliateMultiplier ?? 1.0,
            // Recipe package flags (default false = no package applied)
            stressShield:   room.recipe?.stressShield   ?? false,
            bloomBoost:     room.recipe?.bloomBoost     ?? false,
            yieldMaximizer: room.recipe?.yieldMaximizer ?? false,
          },
          automation: {
            enabled: room.automation?.enabled ?? false,
            managerId: room.automation?.managerId ?? null,
            defaultStrainId: room.automation?.defaultStrainId ?? null,
            defaultPlantCount: room.automation?.defaultPlantCount ?? 10,
            recipeId: room.automation?.recipeId ?? null,
          },
          equipment: {
            ...room.equipment,
            // Backfill health (added after initial release) so effectiveEfficiency never crashes on old saves
            hvac: {
              ...room.equipment?.hvac,
              health: room.equipment?.hvac?.health ?? 100,
            },
            // Greenhouse defaults lights to not installed; indoor/industrial default to installed
            lights: {
              ...room.equipment?.lights,
              health:    room.equipment?.lights?.health    ?? 100,
              installed: room.equipment?.lights?.installed ?? (room.type !== 'greenhouse'),
            },
            ...(room.equipment?.co2sys ? { co2sys: { ...room.equipment.co2sys, health: room.equipment.co2sys.health ?? 100 } } : {}),
            ...(room.equipment?.irrigation ? { irrigation: { ...room.equipment.irrigation, health: room.equipment.irrigation.health ?? 100 } } : {}),
          },
        })),
        ui: { ...defaults.ui, ...saved.ui, lastSaved: saved.ui?.lastSaved ?? null },
        tutorial: saved.tutorial ?? defaults.tutorial,
      };
      const hasLab = (loaded.rooms || []).some(r => r.id === 'room-lab');
      if (!hasLab) {
        loaded.rooms = [...(loaded.rooms || []), defaults.rooms.find(r => r.id === 'room-lab')];
      }
      // Backfill new purchasable rooms added after initial release
      const NEW_PURCHASABLE_ROOMS = ['room-veg-02', 'room-flower-04', 'room-curing', 'room-processing'];
      NEW_PURCHASABLE_ROOMS.forEach(id => {
        if (!(loaded.rooms || []).some(r => r.id === id)) {
          const template = defaults.rooms.find(r => r.id === id);
          if (template) loaded.rooms = [...loaded.rooms, template];
        }
      });
      // Guard activeResearch against tech nodes that may not exist yet (e.g. branch E added later)
      if (loaded.activeResearch && loaded.activeResearch.nodeId) {
        const { getTechNode } = require('./data/technology');
        if (!getTechNode(loaded.activeResearch.nodeId)) {
          loaded.activeResearch = null;
        }
      }
      // Backfill market prices for extracts (added after initial release)
      if (!loaded.marketPrices.extractRosin) {
        loaded.marketPrices.extractRosin = 150;
        loaded.marketPrices.extractWax = 30;
        loaded.marketPrices.extractShatter = 35;
      }
      // Backfill marketing department state (added with marketing department)
      loaded.marketing = {
        ...defaults.marketing,
        ...(loaded.marketing || {}),
      };
      if (loaded.demandModifier === undefined) {
        loaded.demandModifier = 1.0;
      }
      if (loaded.brandValue === undefined) {
        loaded.brandValue = defaults.brandValue ?? 0;
      }
      // Deduplicate inventory: remove sold/empty items, keep only first valid occurrence per id.
      // Also sync inventoryIdCounter so future harvests never reuse an existing id.
      const seenInvIds = new Set();
      loaded.inventory = (loaded.inventory ?? []).filter(item => {
        if (!item) return false;
        if (item.status === 'sold' || !Number.isFinite(item.grams) || item.grams <= 0) return false;
        if (seenInvIds.has(item.id)) return false;
        seenInvIds.add(item.id);
        return true;
      });
      const maxInvNum = loaded.inventory.reduce((max, item) => {
        const n = parseInt((item.id || '').replace(/\D+/g, ''), 10);
        return isNaN(n) ? max : Math.max(max, n);
      }, inventoryIdCounter);
      inventoryIdCounter = maxInvNum + 1;
      return loaded;
    }

    // ── RESET_GAME ────────────────────────────────────────────────────────────
    case 'RESET_GAME': {
      const { initialGameState } = require('./initialState');
      return { ...initialGameState };
    }

    // ── TUTORIAL ─────────────────────────────────────────────────────────────
    case 'TUTORIAL_DISMISS_WELCOME': {
      const nextStep = checkTutorialCompletion(state.tutorial, 'TUTORIAL_DISMISS_WELCOME', null, state.gameDay);
      return {
        ...state,
        tutorial: { ...state.tutorial, welcomeDismissed: true, currentStep: nextStep ?? state.tutorial.currentStep },
      };
    }
    case 'TUTORIAL_SKIP':
      return { ...state, tutorial: { ...state.tutorial, enabled: false, currentStep: null } };
    case 'TUTORIAL_ADVANCE':
      return { ...state, tutorial: { ...state.tutorial, currentStep: action.payload.step } };
    case 'DISMISS_HINT':
      return { ...state, tutorial: { ...state.tutorial, hintDismissed: true } };

    // ── DEFEND_TAKEOVER ──────────────────────────────────────────────────────
    case 'DEFEND_TAKEOVER': {
      if (state.cash < 500000) return state;
      const takeoverEvent = state.activeEvents.find(e => e.type === 'corporate_takeover' && !e.resolved);
      if (!takeoverEvent) return state;
      return {
        ...state,
        cash: state.cash - 500000,
        corporateSatisfaction: 40, // reset to avoid immediate re-trigger
        corporateDirectiveMissCount: 0,
        activeEvents: state.activeEvents.map(e =>
          e.id === takeoverEvent.id ? { ...e, resolved: true, resolvedDay: state.gameDay } : e
        ),
        notifications: [...state.notifications, {
          id: `notif-defend-takeover-${Date.now()}`,
          message: '🛡️ You paid $500,000 to defend against CannCorp\'s takeover. Independence preserved — for now.',
          type: 'success', day: state.gameDay, read: false,
        }],
      };
    }

    // ── BUILD_LAB ─────────────────────────────────────────────────────────────
    case 'BUILD_LAB': {
      if (state.cash < 15000) return state;
      return {
        ...state,
        cash: state.cash - 15000,
        totalExpenses: state.totalExpenses + 15000,
        rooms: state.rooms.map(r => r.id === 'room-lab' ? { ...r, unlocked: true } : r),
        notifications: [...state.notifications, {
          id: `notif-lab-${Date.now()}`, message: '🔬 Research Lab built! Assign scientists to start generating RP.',
          type: 'milestone', day: state.gameDay, read: false,
        }],
      };
    }

    // ── BUY_LAB_EQUIPMENT ─────────────────────────────────────────────────────
    case 'BUY_LAB_EQUIPMENT': {
      const { equipKey } = action.payload; // 'microscope' | 'pcr' | 'sequencer'
      const lab = state.rooms.find(r => r.id === 'room-lab');
      if (!lab || !lab.unlocked) return state;
      const equip = lab.equipment[equipKey];
      if (!equip || equip.installed || state.cash < equip.cost) return state;
      return {
        ...state,
        cash: state.cash - equip.cost,
        totalExpenses: state.totalExpenses + equip.cost,
        rooms: state.rooms.map(r => r.id === 'room-lab'
          ? { ...r, equipment: { ...r.equipment, [equipKey]: { ...equip, installed: true } } }
          : r
        ),
        notifications: [...state.notifications, {
          id: `notif-labequip-${Date.now()}`,
          message: `🔬 ${equip.label} installed! +${equip.rpBonus} RP/week.`,
          type: 'success', day: state.gameDay, read: false,
        }],
      };
    }

    // ── BUY_STRAIN_RECIPE ─────────────────────────────────────────────────────
    case 'BUY_STRAIN_RECIPE': {
      const { recipeId: buyRecipeId } = action.payload;
      const buyRecipe = getRecipe(buyRecipeId);
      if (!buyRecipe || (state.purchasedRecipes ?? []).includes(buyRecipeId)) return state;
      if (state.cash < buyRecipe.price) return state;
      return {
        ...state,
        cash: state.cash - buyRecipe.price,
        totalExpenses: state.totalExpenses + buyRecipe.price,
        purchasedRecipes: [...(state.purchasedRecipes ?? []), buyRecipeId],
        notifications: [...state.notifications, {
          id: `notif-recipe-${Date.now()}`,
          message: `📋 Purchased: ${buyRecipe.name}`,
          type: 'success',
          day: state.gameDay,
          read: false,
        }],
      };
    }

    // ── BUYBACK_CORPORATE_EQUITY ─────────────────────────────────────────────
    case 'BUYBACK_CORPORATE_EQUITY': {
      if (!state.corporateInvestmentAccepted || state.corporateBoughtOut) return state;
      const buybackPrice = calculateBuybackPrice(state);
      if (state.cash < buybackPrice) return state;
      return {
        ...state,
        cash: state.cash - buybackPrice,
        corporateBoughtOut: true,
        corporateDirective: null,
        corporateChannelRestriction: null,
        corporateSatisfaction: 100,
        reputation: Math.min(100, state.reputation + 5),
        // Resolve any active takeover events
        activeEvents: state.activeEvents.map(e =>
          e.type === 'corporate_takeover' && !e.resolved
            ? { ...e, resolved: true, resolvedDay: state.gameDay }
            : e
        ),
        notifications: [...state.notifications, {
          id: `notif-buyback-${Date.now()}`,
          message: `🎉 You bought out CannCorp's 25% stake for $${buybackPrice.toLocaleString()}! You're independent again. +5 reputation.`,
          type: 'milestone', day: state.gameDay, read: false,
        }],
      };
    }

    // ── ADD_TO_CURE ─────────────────────────────────────────────────────────────
    case 'ADD_TO_CURE': {
      const { inventoryId } = action.payload;
      const curingRoom = state.rooms.find(r => r.id === 'room-curing' && r.unlocked);
      if (!curingRoom) return state;
      if ((curingRoom.curingSlots ?? []).length >= 4) return state;

      const item = state.inventory.find(i => i.id === inventoryId);
      if (!item || item.status === 'sold' || item.status === 'in_curing' || (item.type ?? 'flower') !== 'flower') return state;

      const newSlot = {
        inventoryId: item.id,
        grams: item.grams,
        strainId: item.strainId,
        qualityScore: item.qualityScore,
        terpeneProfile: item.terpeneProfile,
        visualAppeal: item.visualAppeal,
        originalTerpeneProfile: item.terpeneProfile,
        originalVisualAppeal: item.visualAppeal,
        thcAccumulation: item.thcAccumulation,
        curingDay: 0,
        peakDay: null,
        moldRisk: 0,
        status: 'curing',
      };

      return {
        ...state,
        rooms: state.rooms.map(r =>
          r.id === 'room-curing'
            ? { ...r, curingSlots: [...(r.curingSlots ?? []), newSlot] }
            : r
        ),
        inventory: state.inventory.map(i =>
          i.id === inventoryId ? { ...i, status: 'in_curing' } : i
        ),
      };
    }

    // ── PULL_FROM_CURE ───────────────────────────────────────────────────────────
    case 'PULL_FROM_CURE': {
      const { inventoryId } = action.payload;
      const curingRoom = state.rooms.find(r => r.id === 'room-curing');
      if (!curingRoom) return state;

      const slot = (curingRoom.curingSlots ?? []).find(s => s.inventoryId === inventoryId);
      if (!slot) return state;

      const cureBonus = Math.round(
        (slot.terpeneProfile - slot.originalTerpeneProfile) +
        (slot.visualAppeal  - slot.originalVisualAppeal) / 2
      );

      return {
        ...state,
        rooms: state.rooms.map(r =>
          r.id === 'room-curing'
            ? { ...r, curingSlots: (r.curingSlots ?? []).filter(s => s.inventoryId !== inventoryId) }
            : r
        ),
        inventory: state.inventory.map(i =>
          i.id === inventoryId
            ? { ...i, status: 'cured', cureBonus: Math.max(0, cureBonus) }
            : i
        ),
      };
    }

    // ── SET_CURE_RECIPE ──────────────────────────────────────────────────────────
    case 'SET_CURE_RECIPE': {
      const { temperature, humidity } = action.payload;
      return {
        ...state,
        rooms: state.rooms.map(r =>
          r.id === 'room-curing'
            ? { ...r, recipe: { ...r.recipe, temperature, humidity } }
            : r
        ),
      };
    }

    // ── ADD_TO_PROCESSING ────────────────────────────────────────────────────────
    case 'ADD_TO_PROCESSING': {
      const { inventoryId, mode, extractType } = action.payload;
      const procRoom = state.rooms.find(r => r.id === 'room-processing' && r.unlocked);
      if (!procRoom) return state;
      if ((procRoom.processingQueue ?? []).length >= 5) return state;

      const item = state.inventory.find(i => i.id === inventoryId);
      if (!item || item.status === 'sold' || item.status === 'in_processing') return state;

      const relevantTypes = mode === 'extract' ? ['scientist', 'grower'] : ['trimmer', 'grower'];
      const assignedWorkers = (procRoom.assignedWorkers ?? []).map(id => state.workers.find(w => w.id === id)).filter(Boolean);
      const relevantWorker = assignedWorkers.find(w => relevantTypes.includes(w.type));
      const workerSkillSnapshot = relevantWorker?.skill ?? 50;

      const daysRequired = mode === 'extract' ? 4 : 2;
      const hasActive = (procRoom.processingQueue ?? []).some(j => j.status === 'active');

      const newJob = {
        inventoryId: item.id,
        mode,
        extractType: extractType ?? null,
        inputGrams: item.grams,
        daysRequired,
        daysAccumulated: 0,
        workerSkillSnapshot,
        qualitySnapshot:      item.qualityScore,
        thcSnapshot:          item.thcAccumulation,
        terpeneSnapshot:      item.terpeneProfile,
        visualAppealSnapshot: item.visualAppeal,
        strainIdSnapshot:     item.strainId,
        harvestDaySnapshot:   item.harvestDay,
        originalStatus:       item.status,
        status: hasActive ? 'pending' : 'active',
      };

      return {
        ...state,
        rooms: state.rooms.map(r =>
          r.id === 'room-processing'
            ? { ...r, processingQueue: [...(r.processingQueue ?? []), newJob] }
            : r
        ),
        inventory: state.inventory.map(i =>
          i.id === inventoryId ? { ...i, status: 'in_processing' } : i
        ),
      };
    }

    // ── CANCEL_PROCESSING ────────────────────────────────────────────────────────
    case 'CANCEL_PROCESSING': {
      const { inventoryId } = action.payload;
      const procRoom = state.rooms.find(r => r.id === 'room-processing');
      if (!procRoom) return state;

      const job = (procRoom.processingQueue ?? []).find(j => j.inventoryId === inventoryId);
      if (!job) return state;

      const wasActive = job.status === 'active';
      const remaining = (procRoom.processingQueue ?? []).filter(j => j.inventoryId !== inventoryId);

      const promoted = wasActive
        ? remaining.map((j, idx) => idx === 0 && j.status === 'pending' ? { ...j, status: 'active' } : j)
        : remaining;

      return {
        ...state,
        rooms: state.rooms.map(r =>
          r.id === 'room-processing' ? { ...r, processingQueue: promoted } : r
        ),
        inventory: state.inventory.map(i =>
          i.id === inventoryId
            ? { ...i, status: job.originalStatus ?? 'fresh' }
            : i
        ),
      };
    }

    // ── BUY_PROCESSING_EQUIPMENT ─────────────────────────────────────────────────
    case 'BUY_PROCESSING_EQUIPMENT': {
      const { equipId } = action.payload;
      const procRoom = state.rooms.find(r => r.id === 'room-processing');
      if (!procRoom) return state;

      const equip = procRoom.equipment?.[equipId];
      if (!equip || equip.installed) return state;
      if (state.cash < equip.cost) return state;

      return {
        ...state,
        cash: state.cash - equip.cost,
        totalExpenses: (state.totalExpenses ?? 0) + equip.cost,
        rooms: state.rooms.map(r =>
          r.id === 'room-processing'
            ? { ...r, equipment: { ...r.equipment, [equipId]: { ...equip, installed: true } } }
            : r
        ),
      };
    }

    // ── BUY_CURING_EQUIPMENT ──────────────────────────────────────────────────────
    case 'BUY_CURING_EQUIPMENT': {
      const { equipId } = action.payload;
      const curingRoom = state.rooms.find(r => r.id === 'room-curing');
      if (!curingRoom) return state;

      const equip = curingRoom.equipment?.[equipId];
      if (!equip || equip.installed) return state;
      if (state.cash < equip.cost) return state;

      return {
        ...state,
        cash: state.cash - equip.cost,
        totalExpenses: (state.totalExpenses ?? 0) + equip.cost,
        rooms: state.rooms.map(r =>
          r.id === 'room-curing'
            ? { ...r, equipment: { ...r.equipment, [equipId]: { ...equip, installed: true } } }
            : r
        ),
      };
    }

    // ── SET_MARKETING_BUDGET ──────────────────────────────────────────────────
    case 'SET_MARKETING_BUDGET': {
      if (!hasMarketingDept(state)) return state;
      const budget = Math.max(0, Math.round(action.payload.amount || 0));
      return {
        ...state,
        marketing: { ...state.marketing, weeklyBudget: budget },
      };
    }

    // ── LAUNCH_CAMPAIGN ─────────────────────────────────────────────────────
    case 'LAUNCH_CAMPAIGN': {
      const { templateId } = action.payload;
      const mkt = state.marketing;
      const template = CAMPAIGN_TEMPLATES.find(t => t.id === templateId);
      if (!template) return state;
      // Tech gate
      if (template.techRequired && !state.unlockedTech?.includes(template.techRequired)) return state;
      // Max concurrent campaigns
      if ((mkt.activeCampaigns || []).length >= (mkt.maxCampaignSlots || 3)) return state;
      // Cost check
      if (state.cash < template.cost) return state;
      // Must be in available slots
      if (!(mkt.availableCampaignSlots || []).some(s => s.id === templateId)) return state;

      const campaign = {
        id: `campaign-${Date.now()}-${templateId}`,
        templateId: template.id,
        name: template.name,
        daysRemaining: template.durationDays,
        launchDay: state.gameDay,
      };

      return {
        ...state,
        cash: state.cash - template.cost,
        totalExpenses: (state.totalExpenses || 0) + template.cost,
        marketing: {
          ...mkt,
          activeCampaigns: [...(mkt.activeCampaigns || []), campaign],
          availableCampaignSlots: (mkt.availableCampaignSlots || []).filter(s => s.id !== templateId),
        },
        notifications: [...state.notifications, {
          id: `notif-campaign-launch-${Date.now()}`,
          message: `📣 Campaign launched: "${template.name}" — $${template.cost} invested.`,
          type: 'success', day: state.gameDay, read: false,
        }].slice(-50),
      };
    }

    // ── CANCEL_CAMPAIGN ─────────────────────────────────────────────────────
    case 'CANCEL_CAMPAIGN': {
      const { campaignId } = action.payload;
      const mkt = state.marketing;
      const campaign = (mkt.activeCampaigns || []).find(c => c.id === campaignId);
      if (!campaign) return state;

      const template = CAMPAIGN_TEMPLATES.find(t => t.id === campaign.templateId);
      const refund = template ? Math.round(template.cost * 0.5) : 0;

      return {
        ...state,
        cash: state.cash + refund,
        marketing: {
          ...mkt,
          activeCampaigns: (mkt.activeCampaigns || []).filter(c => c.id !== campaignId),
        },
        notifications: [...state.notifications, {
          id: `notif-campaign-cancel-${Date.now()}`,
          message: `🚫 Campaign "${campaign.name}" cancelled. $${refund} refunded.`,
          type: 'info', day: state.gameDay, read: false,
        }].slice(-50),
      };
    }

    // ── SCHEDULE_TOUR ───────────────────────────────────────────────────────
    case 'SCHEDULE_TOUR': {
      if (!hasMarketingDept(state)) return state;
      const mkt = state.marketing;
      // Monthly tour cap
      const currentMonthKey = `${Math.floor(state.gameDay / 30)}`;
      const monthCount = mkt.lastTourMonthKey === currentMonthKey ? (mkt.monthlyTourCount || 0) : 0;
      if (monthCount >= MAX_TOURS_PER_MONTH) return state;
      if (state.cash < TOUR_FEE) return state;

      const tourDay = state.gameDay + 3; // tours resolve in 3 days
      const tour = {
        id: `tour-${Date.now()}`,
        scheduledDay: tourDay,
        fee: TOUR_FEE,
      };

      return {
        ...state,
        cash: state.cash - TOUR_FEE,
        totalExpenses: (state.totalExpenses || 0) + TOUR_FEE,
        marketing: {
          ...mkt,
          scheduledTours: [...(mkt.scheduledTours || []), tour],
          monthlyTourCount: monthCount + 1,
          lastTourMonthKey: currentMonthKey,
        },
        notifications: [...state.notifications, {
          id: `notif-tour-sched-${Date.now()}`,
          message: `🏭 Facility tour scheduled for Day ${tourDay}. Cost: $${TOUR_FEE}.`,
          type: 'info', day: state.gameDay, read: false,
        }].slice(-50),
      };
    }

    // ── CANCEL_TOUR ─────────────────────────────────────────────────────────
    case 'CANCEL_TOUR': {
      const { tourId } = action.payload;
      const mkt = state.marketing;
      const tour = (mkt.scheduledTours || []).find(t => t.id === tourId);
      if (!tour) return state;

      const refund = Math.round((tour.fee || TOUR_FEE) * TOUR_CANCEL_REFUND);
      return {
        ...state,
        cash: state.cash + refund,
        marketing: {
          ...mkt,
          scheduledTours: (mkt.scheduledTours || []).filter(t => t.id !== tourId),
          monthlyTourCount: Math.max(0, (mkt.monthlyTourCount || 0) - 1),
        },
        notifications: [...state.notifications, {
          id: `notif-tour-cancel-${Date.now()}`,
          message: `🚫 Tour cancelled. $${refund} refunded.`,
          type: 'info', day: state.gameDay, read: false,
        }].slice(-50),
      };
    }

    // ── RESPOND_TO_PR ───────────────────────────────────────────────────────
    case 'RESPOND_TO_PR': {
      const { crisisId, response } = action.payload;
      const mkt = state.marketing;
      const crisis = (mkt.prCrises || []).find(c => c.id === crisisId);
      if (!crisis) return state;

      const { resolvePRCrisis } = require('./systems/marketing');
      const result = resolvePRCrisis(crisis, response);

      return {
        ...state,
        cash: state.cash + (result.cashDelta || 0),
        brandValue: Math.max(0, Math.min(100, state.brandValue + (result.brandDelta || 0))),
        reputation: Math.max(0, Math.min(100, state.reputation + (result.repDelta || 0))),
        marketing: {
          ...mkt,
          prCrises: (mkt.prCrises || []).filter(c => c.id !== crisisId),
        },
        notifications: [...state.notifications, {
          id: `notif-pr-resolve-${Date.now()}`,
          message: result.message,
          type: result.brandDelta >= 0 ? 'success' : 'warning', day: state.gameDay, read: false,
        }].slice(-50),
      };
    }

    // ── SET_PACKAGE_TIER ────────────────────────────────────────────────────
    case 'SET_PACKAGE_TIER': {
      const { inventoryId, tier } = action.payload;
      const validTier = Math.max(0, Math.min(3, tier || 0));
      const item = state.inventory.find(i => i.id === inventoryId);
      if (!item) return state;
      // Only packaged items can have their tier changed (tier 0 = downgrade to bulk)
      if (!item.packaged && validTier > 0) return state;
      return {
        ...state,
        inventory: state.inventory.map(i =>
          i.id === inventoryId ? { ...i, packageTier: validTier } : i
        ),
      };
    }

    default:
      return state;
  }
}

// ── Exported reducer — applies tutorial completion after player actions ───────
export function gameReducer(state, action) {
  const nextState = gameReducerCore(state, action);
  // TICK handles its own tutorial advances inline; skip here to avoid double-advance
  if (action.type === 'TICK' || !nextState.tutorial?.enabled) return nextState;
  const advance = checkTutorialCompletion(nextState.tutorial, action.type, action.payload, nextState.gameDay);
  if (advance !== null) {
    return { ...nextState, tutorial: { ...nextState.tutorial, currentStep: advance } };
  }
  return nextState;
}

// ── Weekly processing (called when a new week begins) ────────────────────────
function processWeek(state) {
  // 1. Process finances
  const financeUpdates = processWeeklyFinances(state);

  // 2. Update market prices
  const marketUpdates = updateMarketPrices(state);

  // 3. Process wages
  let { updatedWorkers } = processWages(state.workers, state.cash);

  // 3b. Manager quality effects on facility morale
  let managerMoraleEffect = 0;
  state.rooms.forEach(room => {
    const auto = room.automation;
    if (!auto?.enabled || !auto.managerId) return;
    const mgr = updatedWorkers.find(w => w.id === auto.managerId);
    if (!mgr) return;
    if (mgr.skill < 30) managerMoraleEffect -= 5;
    else if (mgr.skill < 50) managerMoraleEffect -= 2;
    else if (mgr.skill >= 75) managerMoraleEffect += 1;
  });
  if (managerMoraleEffect !== 0) {
    updatedWorkers = updatedWorkers.map(w =>
      w.type !== 'manager'
        ? { ...w, morale: Math.max(0, Math.min(100, w.morale + managerMoraleEffect)) }
        : w
    );
  }

  // 4. Research point accumulation
  const { calculateWeeklyRP } = require('./systems/labor');
  const weeklyRP = calculateWeeklyRP(state.workers, state.rooms.find(r => r.id === 'room-lab'));
  const labFailure = state.activeEvents?.find(e => e.type === 'lab_equipment_failure' && !e.resolved);
  const effectiveRP = labFailure ? Math.floor(weeklyRP * 0.5) : weeklyRP;
  let newResearch = state.activeResearch;
  let newUnlockedTech = [...state.unlockedTech];
  let researchNotif = [];
  if (newResearch) {
    const accumulated = newResearch.pointsAccumulated + effectiveRP + state.researchPoints;
    if (accumulated >= newResearch.pointsRequired) {
      newUnlockedTech = [...newUnlockedTech, newResearch.nodeId];
      const { getTechNode } = require('./data/technology');
      const node = getTechNode(newResearch.nodeId);
      researchNotif = [{ id: `notif-research-done-${Date.now()}`, message: `✅ Research complete: ${node?.name}!`, type: 'success', day: state.gameDay, read: false }];
      newResearch = null;
    } else {
      newResearch = { ...newResearch, pointsAccumulated: accumulated };
    }
  }

  // 5. Check contract deadlines
  const { updated: updatedContracts, failed: failedContracts } = checkContractDeadlines(state.activeContracts, state.gameDay);
  const contractPenalties = failedContracts.reduce((sum, c) => {
    return sum + Math.round(c.gramsRequired * (state.marketPrices.wholesale || 2.50) * c.penaltyPct);
  }, 0);
  const contractNotifs = failedContracts.map(c => ({
    id: `notif-contract-fail-${c.id}`,
    message: `❌ Contract failed: ${c.label} — Penalty $${contractPenalties.toLocaleString()}`,
    type: 'danger',
    day: state.gameDay,
    read: false,
  }));

  // 6. Refresh job market
  const newJobMarket = refreshJobMarket(state.stage);

  // 7. Generate new available contracts
  const templates = getAvailableTemplates(state.stage);
  const contractCount = Math.min(2, templates.length);
  const newContracts = Array.from({ length: contractCount }, () => {
    const tpl = templates[Math.floor(Math.random() * templates.length)];
    return generateContract(tpl.id, state.gameDay);
  }).filter(Boolean);
  const existingAvailable = state.availableContracts.slice(-3); // keep up to 3 old ones

  // 8. Brand value passive growth + weekly marketing tick
  const hasMktDept = newUnlockedTech.includes('D4');
  const mktTick = weeklyMarketingTick(
    { ...state, unlockedTech: newUnlockedTech },
    Math.random
  );
  const mktBrandDelta = Math.min(WEEKLY_BRAND_CAP, (hasMktDept ? 2 : 0) + (mktTick.brandDelta || 0));
  const newBrandValue = Math.max(0, Math.min(100, state.brandValue + mktBrandDelta));

  // 9. Stage progression check
  const stageCheck = checkStageProgression({ ...state, totalRevenue: state.totalRevenue + (financeUpdates.totalRevenue || 0) });
  const advancingToStage = stageCheck && stageCheck > state.stage ? stageCheck : null;
  const stageNotif = advancingToStage ? [{
    id: `notif-stage-${Date.now()}`,
    message: `🏆 You've reached Stage ${advancingToStage}! New opportunities unlocked.`,
    type: 'success',
    day: state.gameDay,
    read: false,
  }] : [];
  // Trigger corporate investment offer when advancing to Stage 2
  const corporateOfferNotif = advancingToStage === 2 && !state.corporateInvestmentAccepted ? [{
    id: `notif-corp-offer-${Date.now()}`,
    message: '💼 CannCorp Inc. is interested — they\'re offering $1,000,000 for 25% equity. Review the offer.',
    type: 'milestone',
    day: state.gameDay,
    read: false,
  }] : [];
  const newCorporateOfferAvailable = advancingToStage === 2 && !state.corporateInvestmentAccepted
    ? true
    : state.corporateOfferAvailable;

  // 10. Compliance score slow recovery
  const newComplianceScore = Math.min(100, state.complianceScore + 1);

  // 11. Auto-expire unresolved events older than 7 days
  // Damaging events (pests, power outage) have already dealt their damage each tick.
  // After 7 days of no player action, auto-close them with a partial fine/cost.
  // Corporate takeover uses 28-day deadline instead of 7.
  const EVENT_EXPIRY_DAYS = 7;
  const expiredEvents = state.activeEvents.filter(
    e => !e.resolved && e.type !== 'corporate_takeover' && (state.gameDay - e.startDay) >= EVENT_EXPIRY_DAYS
  );
  let autoResolvedEvents = state.activeEvents.map(e => {
    if (!e.resolved && e.type !== 'corporate_takeover' && (state.gameDay - e.startDay) >= EVENT_EXPIRY_DAYS) {
      return { ...e, resolved: true, resolvedDay: state.gameDay, autoExpired: true };
    }
    return e;
  });
  const expiryNotifs = expiredEvents.map(e => ({
    id: `notif-expire-${e.id}`,
    message: `⏱ ${e.label} in ${e.roomName || 'facility'} has passed. Situation resolved itself.`,
    type: 'info',
    day: state.gameDay,
    read: false,
  }));

  // Check corporate takeover deadline (28 days) — triggers shutdown if not defended
  let corpTakeoverShutdown = false;
  let corpTakeoverShutdownReason = null;
  autoResolvedEvents = autoResolvedEvents.map(e => {
    if (e.type === 'corporate_takeover' && !e.resolved && (state.gameDay - e.startDay) >= 28) {
      corpTakeoverShutdown = true;
      corpTakeoverShutdownReason = 'CannCorp has assumed operational control. Your grow is no longer your own.';
      return { ...e, resolved: true, resolvedDay: state.gameDay, autoExpired: true };
    }
    return e;
  });

  // ── 12. Corporate Processing ──────────────────────────────────────────────
  const corpInvested = state.corporateInvestmentAccepted && !state.corporateBoughtOut;
  let corpSatisfaction = state.corporateSatisfaction;
  let corpDirective = state.corporateDirective;
  let corpDirectiveHistory = [...state.corporateDirectiveHistory];
  let corpMissCount = state.corporateDirectiveMissCount;
  let corpRestriction = state.corporateChannelRestriction;
  let corpNotifs = [];
  let corpRepChange = 0;
  let corpCashChange = 0;
  let corpBrandBoost = 0;
  let corpTakeoverEvent = null;

  if (corpInvested) {
    // Passive satisfaction recovery
    corpSatisfaction = Math.min(100, corpSatisfaction + 1);

    // Check directive deadline
    if (corpDirective && state.gameDay >= corpDirective.deadline) {
      const result = evaluateDirectiveCompletion({ ...state, corporateDirective: corpDirective });
      if (result && result.success) {
        corpSatisfaction = Math.min(100, corpSatisfaction + 5);
        corpMissCount = 0;
        corpDirectiveHistory.push({ ...corpDirective, result: 'success' });
        corpNotifs.push({
          id: `notif-corp-directive-${Date.now()}`,
          message: `✅ CannCorp directive completed: "${corpDirective.description}"`,
          type: 'success', day: state.gameDay, read: false,
        });
      } else {
        corpSatisfaction = Math.max(0, corpSatisfaction - 15);
        corpMissCount++;
        corpDirectiveHistory.push({ ...corpDirective, result: 'failed' });
        // Escalating penalties
        if (corpMissCount >= 3) {
          corpRepChange -= 15;
          corpCashChange -= 25000;
          corpNotifs.push({
            id: `notif-corp-directive-${Date.now()}`,
            message: `❌ CannCorp directive FAILED: "${corpDirective.description}" — $25,000 fine and -15 reputation. Board is furious.`,
            type: 'danger', day: state.gameDay, read: false,
          });
        } else if (corpMissCount === 2) {
          corpRepChange -= 10;
          corpCashChange -= 10000;
          corpNotifs.push({
            id: `notif-corp-directive-${Date.now()}`,
            message: `❌ CannCorp directive FAILED: "${corpDirective.description}" — $10,000 fine and -10 reputation.`,
            type: 'warning', day: state.gameDay, read: false,
          });
        } else {
          corpRepChange -= 5;
          corpNotifs.push({
            id: `notif-corp-directive-${Date.now()}`,
            message: `⚠️ CannCorp directive missed: "${corpDirective.description}" — -5 reputation.`,
            type: 'warning', day: state.gameDay, read: false,
          });
        }
      }
      corpDirective = null; // clear for next generation
    }

    // Generate new directive every 13 weeks
    if (state.gameWeek > 0 && state.gameWeek % 13 === 0 && !corpDirective) {
      corpDirective = generateDirective(state);
      corpNotifs.push({
        id: `notif-corp-new-directive-${Date.now()}`,
        message: `💼 New CannCorp directive: "${corpDirective.description}" — deadline: Day ${corpDirective.deadline}`,
        type: 'info', day: state.gameDay, read: false,
      });
    }

    // Update directive progress for weekly-check types
    if (corpDirective) {
      if (corpDirective.type === 'maintain_reputation') {
        const currentRep = state.reputation + corpRepChange;
        if (currentRep >= corpDirective.target) {
          corpDirective = { ...corpDirective, progress: corpDirective.progress + 1 };
        } else {
          corpDirective = { ...corpDirective, progress: 0 }; // reset streak
        }
      }
      if (corpDirective.type === 'hire_scientist') {
        corpDirective = { ...corpDirective, progress: checkDirectiveProgress(state, corpDirective) };
      }
      if (corpDirective.type === 'start_research') {
        corpDirective = { ...corpDirective, progress: checkDirectiveProgress(state, corpDirective) };
      }
    }

    // Expire channel restrictions
    if (corpRestriction && state.gameDay >= corpRestriction.until) {
      corpRestriction = null;
      corpNotifs.push({
        id: `notif-corp-restrict-end-${Date.now()}`,
        message: '💼 CannCorp channel restriction has been lifted.',
        type: 'info', day: state.gameDay, read: false,
      });
    }

    // Board pressure thresholds
    const prevLevel = getSatisfactionLevel(state.corporateSatisfaction);
    const newLevel = getSatisfactionLevel(corpSatisfaction);

    if (newLevel === 'concerned' && prevLevel === 'satisfied') {
      corpNotifs.push({
        id: `notif-corp-concerned-${Date.now()}`,
        message: '💼 CannCorp board is growing concerned about your performance.',
        type: 'warning', day: state.gameDay, read: false,
      });
    }

    if (newLevel === 'hostile' && prevLevel !== 'hostile' && prevLevel !== 'takeover') {
      if (!corpRestriction) {
        corpRestriction = { channel: 'blackmarket', type: 'ban', until: state.gameDay + 91 };
        corpNotifs.push({
          id: `notif-corp-hostile-${Date.now()}`,
          message: '🚫 CannCorp has BANNED black market sales for the next quarter. Board satisfaction critical.',
          type: 'danger', day: state.gameDay, read: false,
        });
      }
    }

    if (newLevel === 'takeover' && prevLevel !== 'takeover') {
      // Check if takeover event already active
      const takeoverActive = autoResolvedEvents.some(e => e.type === 'corporate_takeover' && !e.resolved);
      if (!takeoverActive) {
        corpNotifs.push({
          id: `notif-corp-takeover-${Date.now()}`,
          message: '🚨 HOSTILE TAKEOVER THREAT! CannCorp is moving to seize control. Raise $500,000 within 28 days or lose your operation!',
          type: 'critical', day: state.gameDay, read: false,
        });
        corpTakeoverEvent = {
          id: `event-takeover-${Date.now()}`,
          type: 'corporate_takeover',
          label: 'CannCorp Hostile Takeover',
          icon: '🏢',
          description: 'CannCorp is attempting a hostile takeover. Pay $500,000 to defend your independence.',
          severity: 'critical',
          resolutionCost: 500000,
          damagePerTick: 0,
          startDay: state.gameDay,
          resolved: false,
          resolvedDay: null,
        };
      }
    }

    // Brand value boost from corporate backing
    corpBrandBoost = 1; // +1/week on top of other bonuses
  }

  // financeUpdates.cash already deducted wages (via calculateWeeklyBurn which includes wages).
  // Only subtract contractPenalties separately — do NOT subtract wageCost again here.
  const totalCashChange = financeUpdates.cash - state.cash - contractPenalties + corpCashChange + (mktTick.cashDelta || 0);

  const newRep = Math.max(
    corpInvested ? 10 : 0,
    Math.min(100, state.reputation + corpRepChange)
  );

  const finalActiveEvents = corpTakeoverEvent
    ? [...autoResolvedEvents, corpTakeoverEvent]
    : autoResolvedEvents;

  // ── Daily simulation on week-advance days (runs here since TICK returns early on week-advance) ─
  const weekCuringResult = processCuringDay(state);
  const weekProcResult   = processProcessingDay(weekCuringResult);
  const weekDayRooms     = weekProcResult.rooms;
  const weekDayInventory = weekProcResult.inventory;
  const weekDayNotifs    = weekProcResult.notifications;

  return {
    ...state,
    tick: state.tick,
    gameDay: state.gameDay,
    gameWeek: state.gameWeek,
    cash: state.cash + totalCashChange,
    cashHistory: financeUpdates.cashHistory,
    totalRevenue: state.totalRevenue,
    totalExpenses: financeUpdates.totalExpenses + contractPenalties + Math.abs(Math.min(0, corpCashChange)),
    weeklyBurn: financeUpdates.weeklyBurn,
    rooms: weekDayRooms,
    inventory: weekDayInventory,
    workers: updatedWorkers,
    activeResearch: newResearch,
    unlockedTech: newUnlockedTech,
    researchPoints: newResearch ? 0 : (state.researchPoints + effectiveRP),
    activeContracts: updatedContracts,
    availableContracts: [...existingAvailable, ...newContracts],
    contractHistory: [...state.contractHistory, ...failedContracts],
    jobMarket: newJobMarket,
    brandValue: Math.min(100, newBrandValue + corpBrandBoost),
    demandModifier: mktTick.demandModifier,
    marketing: mktTick.marketing,
    stage: stageCheck || state.stage,
    reputation: newRep,
    complianceScore: newComplianceScore,
    ...marketUpdates,
    activeEvents: finalActiveEvents,
    corporateOfferAvailable: newCorporateOfferAvailable,
    corporateSatisfaction: corpSatisfaction,
    corporateDirective: corpDirective,
    corporateDirectiveHistory: corpDirectiveHistory.slice(-20),
    corporateDirectiveMissCount: corpMissCount,
    corporateChannelRestriction: corpRestriction,
    isShutDown: state.isShutDown || corpTakeoverShutdown,
    shutdownReason: corpTakeoverShutdown ? corpTakeoverShutdownReason : state.shutdownReason,
    notifications: [
      ...weekDayNotifs,
      { id: `notif-week-${state.gameWeek}`, message: `📅 Week ${state.gameWeek} complete. Burn: $${financeUpdates.weeklyBurn.toLocaleString()}`, type: 'info', day: state.gameDay, read: false },
      ...contractNotifs,
      ...researchNotif,
      ...stageNotif,
      ...corporateOfferNotif,
      ...expiryNotifs,
      ...corpNotifs,
    ].slice(-50),
  };
}
